/**
 * simulation_engine.js — Pure JS simulation engine for SCADA.
 * No React, no MQTT. Generates realistic liveValues for all nodes each tick.
 *
 * Two-tier architecture:
 *   1. Graph topology: compute flowFraction (0–1) per node using ReactFlow edges
 *   2. Per-node physics: generate signal values using flowFraction
 */

const TICK_DURATION_S = 1.5; // matches setInterval(1500ms) in scada_editor

export const ELEMENT_SIM_DEFAULTS = {
    ribbon_blender:  { sim_cycle_on: 1800,  sim_cycle_off: 300  },
    cooler_blender:  { sim_cycle_on: 1800,  sim_cycle_off: 300  },
    pump:            { sim_cycle_on: 3600,  sim_cycle_off: 600  },
    cyclone:         { sim_cycle_on: 3600,  sim_cycle_off: 300  },
    motor:           { sim_cycle_on: 14400, sim_cycle_off: 1800 },
    compressor:      { sim_cycle_on: 14400, sim_cycle_off: 1800 },
    chiller:         { sim_cycle_on: 28800, sim_cycle_off: 3600 },
    bucket_elevator: { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    screw_conveyor:  { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    granulator:      { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    hopper_screw:    { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    boiler:          { sim_cycle_on: 28800, sim_cycle_off: 3600 },
    water_heater:    { sim_cycle_on: 28800, sim_cycle_off: 3600 },
    v_blender:       { sim_cycle_on: 1800,  sim_cycle_off: 300  },
    sieve:           { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    belt_conveyor:   { sim_cycle_on: 7200,  sim_cycle_off: 600  },
    packager_single: { sim_cycle_on: 28800, sim_cycle_off: 600  },
    packager_multi:  { sim_cycle_on: 28800, sim_cycle_off: 600  },
    packager_doypack:{ sim_cycle_on: 28800, sim_cycle_off: 600  },
    box_former:      { sim_cycle_on: 28800, sim_cycle_off: 600  },
    strip_folder:    { sim_cycle_on: 28800, sim_cycle_off: 600  },
    box_sealer:      { sim_cycle_on: 28800, sim_cycle_off: 600  },
    cip_skid:        { sim_cycle_on: 3600,  sim_cycle_off: 28800 },
    _default:        { sim_cycle_on: 3600,  sim_cycle_off: 600  },
};

// Base physical values per machine type
const BASES = {
    motor:           { speed: 1450, current: 12 },
    pump:            { speed: 980,  current: 8,  pressure: 6  },
    compressor:      { speed: 1200, current: 22, pressure: 8  },
    chiller:         { temp_supply: 7, temp_return: 12, pressure_high: 16, pressure_low: 4.2 },
    cyclone:         { speed: 2800, current: 15 },
    ribbon_blender:  { speed: 45,   current: 18 },
    cooler_blender:  { speed: 60,   current: 20, temp: 65 },
    granulator:      { speed: 350,  current: 25 },
    screw_conveyor:  { speed: 120,  current: 9  },
    bucket_elevator: { speed: 75,   current: 11 },
    hopper_screw:    { speed: 95,   current: 7  },
    boiler:          { pressure: 8, temp: 180   },
    water_heater:    { temp: 65                 },
    v_blender:       { speed: 12,   current: 15 },
    sieve:           { speed: 1450, current: 6  },
    belt_conveyor:   { speed: 45,   current: 8  },
    box_former:      { speed: 30,   current: 5  },
    box_sealer:      { speed: 30,   current: 4  },
};

function cycleToTicks(seconds, speedup) {
    return Math.max(1, Math.round(seconds / (TICK_DURATION_S * speedup)));
}

/**
 * Initialize simulation state for one node.
 * Call this when entering simulation mode or when a new node is dropped.
 */
export function initSimState(elementType, config, _nodeId) {
    const simCfg = config?.sim || {};
    const initialLevel = simCfg.sim_initial_level;
    // Batch blenders and elevator-fed hoppers start empty; tanks/silos start at 80% unless configured
    const rampValue = initialLevel != null ? parseFloat(initialLevel)
        : (['ribbon_blender', 'cooler_blender', 'v_blender', 'hopper_screw', 'screw_conveyor'].includes(elementType) ? 0 : 80);
    // D2+D3: Initialize levelKg eagerly when capacity is known
    const capacityKg = parseFloat(simCfg.sim_capacity_kg ?? 0) || parseFloat(simCfg.sim_batch_capacity_kg ?? 0);
    const levelKg = capacityKg > 0 ? rampValue * capacityKg / 100 : null;
    return {
        phase: Math.random() * Math.PI * 2,
        tick: 0,
        frozenSignals: new Set(),     // manually toggled by user click
        rampValue,
        rampDir: Math.random() > 0.5 ? 1 : -1,
        boolTimers: {},               // { [key]: simSecondsRemaining }
        currentBoolValues: {},        // { [key]: 0|1 }
        errorCooldown: 0,
        prevState: 1,
        startupInrushTicks: 0,
        _count: 0,
        levelKg,                  // kg-based level (non-null when capacity is configured)
        packagerBufferKg: null,   // internal hopper buffer for packagers connected to elevator
        requestQueue: [],         // ordered list of downstream nodeIds requesting (elevator only)
        // Process recipe (Feature 3)
        recipeStep: 0,
        stepTicksRemaining: 0,
        recipeStoppedTicks: 0,    // simulated seconds machine has been OFF (grace-period reset)
        // Startup offset (Feature 1)
        startupOffsetRemaining: undefined,
        // Estimated consumption (Feature 6)
        consumptionSignalWasActive: false,
    };
}

// ── Tier 1: Graph topology ─────────────────────────────────────────────────

function computeFlowFractions(allNodes, edges) {
    // Pre-compute adjacency list: O(E) once instead of O(E) per recursive call.
    const nodeMap = {};
    const outEdgeMap = {};
    allNodes.forEach(n => { nodeMap[n.id] = n; outEdgeMap[n.id] = []; });
    edges.forEach(e => { if (outEdgeMap[e.source]) outEdgeMap[e.source].push(e); });

    function nodeConductance(node) {
        const type = node.type;
        const lv = node.data?.liveValues || {};
        const threshold = parseFloat(node.data?.config?.threshold ?? 0.5);
        const stateVal = parseFloat(lv.state ?? NaN);
        if (['valve_gate', 'valve_ball', 'valve_butterfly', 'valve_3way', 'regulator'].includes(type)) {
            if (isNaN(stateVal)) return 1; // unknown → assume open (avoids false blockages at startup)
            if (type === 'regulator') return Math.min(1, Math.max(0, stateVal / 100));
            return stateVal >= threshold ? 1 : 0;
        }
        if (['pump', 'motor', 'compressor', 'chiller', 'hopper_screw', 'screw_conveyor', 'bucket_elevator'].includes(type)) {
            if (isNaN(stateVal)) return 1;
            return stateVal >= threshold ? 1 : 0;
        }
        return 1; // passive elements always pass flow through
    }

    // DFS with backtracking — single shared Set, no cloning per call.
    // Uses MAX over parallel branches: any open downstream path keeps flow alive.
    // Series conductance = product (both ends of a pipe must be open).
    // Cycles return 0 (conservative: break the loop rather than assume open).
    const visited = new Set();
    function downstreamConductance(nodeId) {
        if (visited.has(nodeId)) return 0; // cycle detected → conservative
        visited.add(nodeId);

        const outEdges = outEdgeMap[nodeId] || [];
        if (outEdges.length === 0) {
            visited.delete(nodeId);
            return 1; // terminal sink — always open
        }

        let maxCond = 0;
        for (const edge of outEdges) {
            const targetNode = nodeMap[edge.target];
            if (!targetNode) continue;
            const tc = nodeConductance(targetNode);
            if (tc === 0) continue; // closed branch — skip recursion entirely
            const fc = tc * downstreamConductance(edge.target);
            if (fc > maxCond) maxCond = fc;
            if (maxCond >= 1) break; // short-circuit: fully open, can't improve
        }

        visited.delete(nodeId); // backtrack — required for diamond patterns
        return maxCond;
    }

    const flowFractions = {};
    allNodes.forEach(node => {
        flowFractions[node.id] = downstreamConductance(node.id);
    });
    return flowFractions;
}

// ── Tier 2b: Material flow (kg-based) ──────────────────────────────────────
// Runs after per-node generation. Handles real product transport between
// bucket_elevator and connected containers / packagers.

const CONTAINER_TYPES = new Set(['tank', 'silo', 'hopper_screw', 'reactor', 'screw_conveyor']);
const PACKAGER_TYPES    = new Set(['packager_single', 'packager_multi', 'packager_doypack']);
const END_OF_LINE_TYPES = new Set(['box_former', 'strip_folder', 'box_sealer']);

/**
 * Output level_cap_N boolean signals from sim_cap_levels thresholds.
 * capCount comes from node.data.config.level_cap_count (general config).
 * sim_cap_levels is an array of % thresholds ordered bottom→top.
 * Default: evenly distribute across [0, 100].
 */
function applyCapSensors(simCfg, levelPct, lv, capCount) {
    if (!capCount || capCount === 0) return;
    const capLevels = simCfg?.sim_cap_levels;
    for (let i = 0; i < capCount; i++) {
        const defaultThreshold = Math.round((i + 1) / capCount * 100);
        const threshold = capLevels?.[i] != null ? parseFloat(capLevels[i]) : defaultThreshold;
        lv[`level_cap_${i + 1}`] = levelPct >= threshold ? 1 : 0;
    }
}

/**
 * Sync a container's kg-based level from rampValue (%), apply elevator delta,
 * clamp, write back rampValue and lv.level, then apply cap sensors.
 */
function syncContainerLevel(node, simState, lv, deltaKg, capCount) {
    const simCfg = node.data?.config?.sim || {};
    // Default 1000 kg if not configured — allows simulation without explicit setup
    const cap = parseFloat(simCfg.sim_capacity_kg) || 1000;

    // Init levelKg from rampValue (tickNode already ran and set rampValue)
    if (simState.levelKg == null) {
        simState.levelKg = simState.rampValue * cap / 100;
    }
    simState.levelKg = Math.min(cap, Math.max(0, simState.levelKg + deltaKg));
    const pct = (simState.levelKg / cap) * 100;
    simState.rampValue = pct; // keep in sync so tickNode ramp starts from right place
    lv.level = pct;
    applyCapSensors(simCfg, pct, lv, capCount);
}

/** Space remaining in a node before it's full (kg). */
function containerSpaceLeft(node, simState) {
    const simCfg = node.data?.config?.sim || {};
    const cap = parseFloat(simCfg.sim_capacity_kg) || 1000;
    if (simState.levelKg == null) simState.levelKg = simState.rampValue * cap / 100;
    return Math.max(0, cap - simState.levelKg);
}

function computeMaterialFlows(allNodes, edges, simStates, newLiveValuesMap, speedup) {
    const nodeMap = {};
    const outEdgeMap = {};
    allNodes.forEach(n => { nodeMap[n.id] = n; outEdgeMap[n.id] = []; });
    edges.forEach(e => { if (outEdgeMap[e.source]) outEdgeMap[e.source].push(e); });
    const simMinPerTick = TICK_DURATION_S * speedup / 60;

    // Track which containers were updated by an elevator this tick
    const elevatorTouchedIds = new Set();

    // ── Elevator transport ──────────────────────────────────────────────────
    allNodes.filter(n => n.type === 'bucket_elevator').forEach(elevator => {
        const simState = simStates[elevator.id];
        if (!simState) return;
        const elevLV = newLiveValuesMap[elevator.id] || {};
        const running = parseFloat(elevLV.state ?? 0) >= 0.5;

        const cfg = elevator.data?.config?.sim || {};
        const godetCount = parseFloat(cfg.sim_godet_count ?? 360);
        const godetKg    = parseFloat(cfg.sim_godet_capacity_kg ?? 3);
        const revMin     = parseFloat(cfg.sim_revolution_min ?? 12);

        if (godetCount <= 0 || godetKg <= 0 || revMin <= 0) return; // unconfigured — skip silently

        // Physics of a bucket elevator fed by N upstream hoppers (typically 2):
        //   T_rev = sim_revolution_min  — minutes for one bucket to complete the full circuit.
        //   Each hopper contributes an equal share of each bucket's load (godetKg / numHoppers).
        //   A vibrator/screw under each hopper doses its portion independently.
        //   If a hopper is empty its portion of the bucket travels empty;
        //   the other hopper cannot compensate beyond its own share.
        //
        //   Nominal load per hopper per tick:
        //     godetCount * (godetKg / numHoppers) / T_rev  * simMinPerTick

        // Upstream nodes feeding INTO elevator (elevator is edge target)
        const upstreamIds = edges.filter(e => e.target === elevator.id).map(e => e.source);
        // Downstream nodes receiving FROM elevator via the main outlet(s) only.
        // The 'return' handle is for buckets that completed a full revolution without
        // discharging — they must dump at the return point to avoid double-filling.
        // Return-connected nodes have their own dedicated delivery section below and
        // must NOT compete with machine hoppers in the main request queue.
        const downstreamIds = edges
            .filter(e => e.source === elevator.id && e.sourceHandle !== 'return')
            .map(e => e.target);

        const upstreamContainers = upstreamIds
            .map(id => ({ id, node: nodeMap[id], st: simStates[id], lv: newLiveValuesMap[id] }))
            .filter(({ node }) => node && CONTAINER_TYPES.has(node.type));
        const numUp = Math.max(1, upstreamContainers.length);

        // Nominal kg each hopper should contribute this tick (its share of the bucket load).
        const nominalPerHopper = running
            ? (godetCount * (godetKg / numUp) / Math.max(0.01, revMin)) * simMinPerTick
            : 0;

        // Physical capacity cap: the elevator chain holds at most godetCount × godetKg.
        // Compute how much room is left in the chain before draining upstream hoppers,
        // so we never pull more material than the buckets can physically carry.
        const maxChainKg      = godetCount * godetKg;
        const currentInTransit = (simState.godetTransitQueue ?? []).reduce((s, k) => s + k, 0);
        const chainSpaceKg    = Math.max(0, maxChainKg - currentInTransit);
        const nominalCapped   = Math.min(nominalPerHopper, chainSpaceKg / numUp);

        // Each hopper is drained independently, capped by what it actually contains.
        // Sum the per-hopper actuals to get total kg physically loaded into buckets this tick.
        let kgThisTick = 0;
        upstreamContainers.forEach(({ id, node, st, lv }) => {
            if (!st || !lv) return;
            const cap     = parseFloat(node.data?.config?.sim?.sim_capacity_kg) || 1000;
            const availKg = Math.max(0, st.levelKg ?? (st.rampValue * cap / 100));
            const loaded  = Math.min(nominalCapped, availKg);
            kgThisTick   += loaded;
            elevatorTouchedIds.add(id);
            const capCount = parseInt(node.data?.config?.level_cap_count ?? 0);
            syncContainerLevel(node, st, lv, -loaded, capCount);
        });

        // Transit delay: the loaded kg (what actually entered the buckets this tick)
        // only arrives at the discharge point after sim_transit_time_s simulated seconds.
        // The first revolution fills the chain; after that, product flows continuously.
        const transitS    = parseFloat(cfg.sim_transit_time_s ?? 0);
        const transitTicks = transitS > 0
            ? Math.max(1, Math.round(transitS / (TICK_DURATION_S * speedup)))
            : 0;
        let kgToDeliver = kgThisTick; // default: instant (no transit delay configured)
        if (transitTicks > 0) {
            if (!simState.godetTransitQueue) simState.godetTransitQueue = [];
            // Pad with zeros if queue is shorter than needed (first tick or speedup decreased)
            while (simState.godetTransitQueue.length < transitTicks)
                simState.godetTransitQueue.unshift(0);
            // Shrink if queue is longer than needed (speedup increased):
            // collapse excess front entries into the first remaining slot so
            // product in transit arrives immediately instead of being stuck.
            while (simState.godetTransitQueue.length > transitTicks) {
                const excess = simState.godetTransitQueue.shift();
                if (excess > 0 && simState.godetTransitQueue.length > 0)
                    simState.godetTransitQueue[0] += excess;
            }
            simState.godetTransitQueue.push(kgThisTick);
            kgToDeliver = simState.godetTransitQueue.shift();
        } else {
            simState.godetTransitQueue = [];
        }

        // Each godet dumps whatever it contains at the discharge point — partial or full.
        // The transit queue already handles timing; no need to hold back partial godets.

        // ── Maintain request queue (FIFO/LIFO ordering by arrival time) ─────
        if (!simState.requestQueue) simState.requestQueue = [];
        const downstreamNodes = downstreamIds.map(id => nodeMap[id]).filter(Boolean);
        // Prune stale queue entries: nodes that are no longer in downstreamNodes
        // (e.g. return-connected nodes removed by sourceHandle filter after a config change).
        const downstreamNodeIds = new Set(downstreamIds);
        simState.requestQueue = simState.requestQueue.filter(id => downstreamNodeIds.has(id));

        downstreamNodes.forEach(n => {
            const nlv = newLiveValuesMap[n.id] || n.data?.liveValues || {};
            const thr = parseFloat(n.data?.config?.threshold ?? 0.5);
            // Requesting is driven by the container's own requesting_product signal
            // (edge-triggered sensor hysteresis computed by tickNode) and downstream demand.
            // Do NOT bypass with a simple level check — the edge-triggered logic honours
            // the configured sim_request_on_sensor / sim_request_off_sensor thresholds.
            const reqProd = parseFloat(nlv.requesting_product ?? 0);
            // If the hopper is OFF (state=0), it's not requesting — skip it entirely.
            const hopperOff = parseFloat(nlv.state ?? 1) < thr;
            // Queue membership is driven ONLY by the container's own requesting_product
            // signal (edge-triggered sensor hysteresis). Downstream demand (packager requesting)
            // does NOT add the hopper to the queue — only the hopper's own level sensors do.
            const isRequesting = !hopperOff && reqProd >= thr;

            if (_dbg) console.log(`[ELEV QUEUE] down=${n.id}(${n.type}) reqProd=${reqProd} off=${hopperOff} → ${isRequesting?'REQ':'---'}`);

            const inQueue = simState.requestQueue.includes(n.id);
            if (isRequesting && !inQueue) {
                simState.requestQueue.push(n.id); // arrived: add to back
                // Track real-time timestamp of last request per downstream node
                if (!simState.lastRequestTime) simState.lastRequestTime = {};
                const prevTs = simState.lastRequestTime[n.id];
                const deltaSec = prevTs ? ((Date.now() - prevTs) / 1000).toFixed(1) : '—';
                const nSimCfg_ = n.data?.config?.sim || {};
                const nCap_ = parseFloat(nSimCfg_.sim_capacity_kg) || 1000;
                const nLvlKg_ = simStates[n.id]?.levelKg;
                const nLvlPct_ = nLvlKg_ != null ? ((nLvlKg_ / nCap_) * 100).toFixed(1) : '?';
                const nCapLvls_ = nSimCfg_.sim_cap_levels;
                const nSensorCnt_ = parseInt(n.data?.config?.level_cap_count ?? 0);
                const onIdx_ = Math.min(parseInt(nSimCfg_.sim_request_on_sensor ?? 0), Math.max(nSensorCnt_ - 1, 0));
                const offIdx_ = Math.min(parseInt(nSimCfg_.sim_request_off_sensor ?? Math.max(nSensorCnt_ - 2, 0)), Math.max(nSensorCnt_ - 1, 0));
                const onThr_ = Array.isArray(nCapLvls_) && onIdx_ < nCapLvls_.length ? parseFloat(nCapLvls_[onIdx_]) : [33,67,90][onIdx_] ?? 67;
                const offThr_ = Array.isArray(nCapLvls_) && offIdx_ < nCapLvls_.length ? parseFloat(nCapLvls_[offIdx_]) : [33,67,90][offIdx_] ?? 67;
                console.log(`%c[ELEV REQUEST] ${n.id}(${n.type}) entered queue — cap=${nCap_}kg lvl=${nLvlPct_}% (${(nLvlKg_??0).toFixed(1)}kg) onThr=${onThr_}% offThr=${offThr_}% sensors=${nSensorCnt_} Δt=${deltaSec}s`, 'color:#F59E0B;font-weight:bold');
                simState.lastRequestTime[n.id] = Date.now();
            } else if (!isRequesting && inQueue) {
                simState.requestQueue = simState.requestQueue.filter(id => id !== n.id); // satisfied: remove
            }
        });

        // Expose queue + last-request timestamps in liveValues for the hover panel
        if (newLiveValuesMap[elevator.id]) {
            newLiveValuesMap[elevator.id]._requestQueue = [...simState.requestQueue];
            newLiveValuesMap[elevator.id]._lastRequestTime = { ...(simState.lastRequestTime || {}) };
        }

        // Fill downstream — FIFO (first to request = first served)
        //                    LIFO (last to request = first served)
        // Concentrate delivery: fill priority node until full, overflow to next.
        const dischargeOrder = cfg.sim_discharge_order || 'fifo';
        const queue = simState.requestQueue;

        // Sort based on discharge mode:
        //   fifo     — first to request = first served (arrival order, front of queue)
        //   lifo     — last to request = first served (reverse arrival, back of queue)
        //   depleted — lowest level % first (ignore arrival order)
        const prioritizedIds = [...downstreamNodes].sort((a, b) => {
            if (dischargeOrder === 'depleted') {
                // Sort by current fill % ascending (most depleted first)
                const stA = simStates[a.id];
                const stB = simStates[b.id];
                const capA = PACKAGER_TYPES.has(a.type) ? parseFloat(a.data?.config?.sim?.sim_packager_hopper_kg ?? 10) : (parseFloat(a.data?.config?.sim?.sim_capacity_kg) || 1000);
                const capB = PACKAGER_TYPES.has(b.type) ? parseFloat(b.data?.config?.sim?.sim_packager_hopper_kg ?? 10) : (parseFloat(b.data?.config?.sim?.sim_capacity_kg) || 1000);
                const fillA = PACKAGER_TYPES.has(a.type) ? (stA?.packagerBufferKg ?? capA * 0.5) : (stA?.levelKg ?? stA?.rampValue * capA / 100);
                const fillB = PACKAGER_TYPES.has(b.type) ? (stB?.packagerBufferKg ?? capB * 0.5) : (stB?.levelKg ?? stB?.rampValue * capB / 100);
                return (fillA / capA) - (fillB / capB);
            }
            // FIFO / LIFO: sort by queue position
            const posA = queue.indexOf(a.id);
            const posB = queue.indexOf(b.id);
            const effA = posA === -1 ? Infinity : posA;
            const effB = posB === -1 ? Infinity : posB;
            return dischargeOrder === 'lifo' ? effB - effA : effA - effB;
        });

        let remainingKg = kgToDeliver;
        let mainActiveTargetId = null; // the ONE hopper receiving product this tick

        // Real elevator: ONE gate open at a time. Find the first requesting
        // hopper with space, deliver to it exclusively. If it fills mid-tick,
        // the excess stays in the chain (available for return or next tick).
        // Next tick the queue rotates to the next hopper automatically.
        for (const node of prioritizedIds) {
            if (remainingKg <= 0) break;
            const st = simStates[node.id];
            const lv = newLiveValuesMap[node.id];
            if (!st) continue;

            if (CONTAINER_TYPES.has(node.type)) {
                // Skip OFF hoppers — don't deliver to them
                const nLv = newLiveValuesMap[node.id] || {};
                if (parseFloat(nLv.state ?? 1) < 0.5) continue;
                // Only deliver to hoppers that are in the request queue
                // (i.e. their own sensors say they need product).
                if (!simState.requestQueue.includes(node.id)) continue;

                const nSimCfg   = node.data?.config?.sim || {};
                const nCap      = parseFloat(nSimCfg.sim_capacity_kg) || 1000;
                const nCapLvls  = nSimCfg.sim_cap_levels;
                const nHighPct  = Array.isArray(nCapLvls) && nCapLvls.length >= 1
                    ? (() => {
                        const offIdx = parseInt(nSimCfg.sim_request_off_sensor ?? nCapLvls.length - 1);
                        return parseFloat(nCapLvls[Math.min(offIdx, nCapLvls.length - 1)] ?? 90);
                    })() : 90;
                const nHighKg   = nCap * nHighPct / 100;
                const nCurrentKg = st.levelKg ?? (st.rampValue * nCap / 100);
                const space = Math.max(0, nHighKg - nCurrentKg);
                if (space <= 0) continue; // full — skip, find one with space

                const kgToAdd = Math.min(remainingKg, space);
                mainActiveTargetId = node.id;
                elevatorTouchedIds.add(node.id);
                const capCount = parseInt(node.data?.config?.level_cap_count ?? 0);
                if (lv) syncContainerLevel(node, st, lv, kgToAdd, capCount);
                remainingKg -= kgToAdd;

                // Post-delivery sensor update: if level now at/above OFF threshold,
                // immediately mark container as not requesting and rotate it to
                // the back of the FIFO queue so the next hopper gets served.
                const newLevelKg = st.levelKg ?? 0;
                const newLevelPct = (newLevelKg / nCap) * 100;
                if (newLevelPct >= nHighPct) {
                    st.containerRequesting = false;
                    if (lv) lv.requesting_product = 0;
                    // Rotate to back of queue — even if drain re-triggers requesting
                    // in the same tick, this hopper won't be first next time.
                    const qi = simState.requestQueue.indexOf(node.id);
                    if (qi !== -1) {
                        simState.requestQueue.splice(qi, 1);
                        simState.requestQueue.push(node.id);
                    }
                }
                break; // ONE gate at a time — remaining stays in chain for return/next tick

            } else if (PACKAGER_TYPES.has(node.type)) {
                const bufCap = parseFloat(node.data?.config?.sim?.sim_packager_hopper_kg ?? 10);
                if (st.packagerBufferKg == null) st.packagerBufferKg = bufCap * 0.5;
                const space = bufCap - st.packagerBufferKg;
                if (space <= 0) continue;
                const kgToAdd = Math.min(remainingKg, space);
                mainActiveTargetId = node.id;
                st.packagerBufferKg += kgToAdd;
                remainingKg -= kgToAdd;
                break; // ONE gate at a time
            }
        }

        let returnDelivered = false;
        let returnDeliveredKg = 0;

        // ── Elevator return: one-at-a-time hysteresis delivery ────────────────────
        // Fills ONE downstream container until its HIGH sensor fires, then moves to
        // the next requesting container. A container requests again only when its
        // MID sensor goes LOW after having been HIGH (hysteresis band prevents
        // constant small top-ups when the container is in the MID–HIGH zone).
        //
        // Return path: any product remaining after the delivery loop goes to return.
        // This happens when: (a) no hopper is in the queue, (b) the active hopper
        // filled mid-tick and surplus had nowhere to go, or (c) godets between
        // the last served gate and the next requesting gate passed the discharge
        // point with no open gate — they dump at return.
        if (remainingKg > 0) {
            const returnEdges = edges.filter(e => e.source === elevator.id && e.sourceHandle === 'return');
            const elevSt = simStates[elevator.id];

            // Resolve each return edge to its first downstream container (skip pass-throughs).
            const seenIds = new Set();
            const returnTargets = [];
            for (const edge of returnEdges) {
                const rt = nodeMap[edge.target];
                if (!rt) continue;
                let container = null;
                if (CONTAINER_TYPES.has(rt.type)) {
                    container = rt;
                } else {
                    for (const de of edges.filter(e2 => e2.source === rt.id)) {
                        const dn = nodeMap[de.target];
                        if (dn && CONTAINER_TYPES.has(dn.type)) { container = dn; break; }
                    }
                }
                if (!container || seenIds.has(container.id)) continue;
                seenIds.add(container.id);
                const cSt = simStates[container.id];
                const cLv = newLiveValuesMap[container.id] || {};
                if (!cSt) continue;
                returnTargets.push({ node: container, st: cSt, lv: cLv });
            }

            // Update hysteresis state per container based on kg-level vs. thresholds.
            // NOTE: lv.level_cap_N is written by syncContainerLevel AFTER this block runs,
            // so we read directly from st.levelKg to avoid a one-tick lag that would
            // freeze the elevator once the first container reaches its HIGH threshold.
            // containerSatisfied = undefined → false (requesting from tick 0).
            for (const { node, st } of returnTargets) {
                const capCount = parseInt(node.data?.config?.level_cap_count ?? 0);
                if (capCount >= 2) {
                    const simCfg2   = node.data?.config?.sim || {};
                    const cap2      = parseFloat(simCfg2.sim_capacity_kg) || 1000;
                    const capLvls2  = simCfg2.sim_cap_levels;
                    const offIdx2   = parseInt(simCfg2.sim_request_off_sensor ?? (Array.isArray(capLvls2) ? capLvls2.length - 2 : 1));
                    const onIdx2    = parseInt(simCfg2.sim_request_on_sensor  ?? 0);
                    const highPct2  = Array.isArray(capLvls2) ? parseFloat(capLvls2[Math.min(offIdx2, capLvls2.length - 1)] ?? 90) : 90;
                    const midPct2   = Array.isArray(capLvls2) ? parseFloat(capLvls2[Math.min(onIdx2,  capLvls2.length - 1)] ?? 50) : 50;
                    const levelPct2 = ((st.levelKg ?? (st.rampValue * cap2 / 100)) / cap2) * 100;
                    if (levelPct2 >= highPct2) st.containerSatisfied = true;
                    if (levelPct2 < midPct2)   st.containerSatisfied = false;
                } else {
                    st.containerSatisfied = (containerSpaceLeft(node, st) <= 0);
                }
            }

            // If current target is now satisfied (or gone), release it.
            if (elevSt.currentTargetId) {
                const cur = returnTargets.find(t => t.node.id === elevSt.currentTargetId);
                if (!cur || cur.st.containerSatisfied) elevSt.currentTargetId = null;
            }

            // Pick the next requesting container if none is active.
            if (!elevSt.currentTargetId) {
                const next = returnTargets.find(t => !t.st.containerSatisfied);
                if (next) elevSt.currentTargetId = next.node.id;
            }

            // Deliver remaining kg to the current target, capped at space to its HIGH threshold.
            if (elevSt.currentTargetId) {
                const cur = returnTargets.find(t => t.node.id === elevSt.currentTargetId);
                if (cur) {
                    const simCfg    = cur.node.data?.config?.sim || {};
                    const capCfg    = cur.node.data?.config || {};
                    const cap       = parseFloat(simCfg.sim_capacity_kg) || 1000;
                    const capCount  = parseInt(capCfg.level_cap_count ?? 0);
                    const capLevels = simCfg.sim_cap_levels;
                    const highPct   = capCount >= 2 && Array.isArray(capLevels) && capLevels.length >= 2
                        ? (() => {
                            const offIdx = parseInt(simCfg.sim_request_off_sensor ?? capLevels.length - 2);
                            return parseFloat(capLevels[Math.min(offIdx, capLevels.length - 1)] ?? 90);
                        })()
                        : 100;
                    const highKg      = cap * highPct / 100;
                    const currentKg   = cur.st.levelKg ?? (cur.st.rampValue * cap / 100);
                    const spaceToHigh = Math.max(0, highKg - currentKg);
                    const toDeliver   = Math.min(remainingKg, spaceToHigh);
                    if (toDeliver > 0) {
                        returnDelivered = true;
                        returnDeliveredKg += toDeliver;
                        syncContainerLevel(cur.node, cur.st, cur.lv, toDeliver, capCount);
                        if (_dbg) console.log(`%c[ELEV RETURN] elevator=${elevator.id} →container=${cur.node.id}(${cur.node.type}) +${toDeliver.toFixed(1)}kg lvl=${(cur.st.levelKg??0).toFixed(1)}/${cap}kg remaining=${(remainingKg-toDeliver).toFixed(1)}kg`, 'color:#A78BFA;font-weight:bold');
                    }
                }
            }

            // Track return target info for hover panel
            if (newLiveValuesMap[elevator.id]) {
                // Build return target info: passthrough node (granulator) → final container
                const returnInfo = [];
                const returnEdges2 = edges.filter(e => e.source === elevator.id && e.sourceHandle === 'return');
                for (const re of returnEdges2) {
                    const passNode = nodeMap[re.target];
                    if (!passNode) continue;
                    let containerId = null;
                    let containerType = null;
                    if (CONTAINER_TYPES.has(passNode.type)) {
                        containerId = passNode.id;
                        containerType = passNode.type;
                    } else {
                        for (const de of edges.filter(e2 => e2.source === passNode.id)) {
                            const dn = nodeMap[de.target];
                            if (dn && CONTAINER_TYPES.has(dn.type)) {
                                containerId = dn.id;
                                containerType = dn.type;
                                break;
                            }
                        }
                    }
                    const cSt = containerId ? simStates[containerId] : null;
                    const cCap = containerId ? (parseFloat(nodeMap[containerId]?.data?.config?.sim?.sim_capacity_kg) || 1000) : 0;
                    const cLvlKg = cSt?.levelKg ?? 0;
                    returnInfo.push({
                        passthroughId: passNode.id,
                        passthroughType: passNode.type,
                        passthroughLabel: passNode.data?.label || passNode.type,
                        containerId,
                        containerType,
                        containerLabel: containerId ? (nodeMap[containerId]?.data?.label || containerType) : null,
                        containerLevelKg: Math.round(cLvlKg),
                        containerCapKg: cCap,
                        containerPct: cCap > 0 ? Math.round(cLvlKg / cCap * 100) : 0,
                    });
                }
                newLiveValuesMap[elevator.id]._returnTargets = returnInfo;
                newLiveValuesMap[elevator.id]._returnKgThisTick = returnDeliveredKg;
            }

        }

        // Emit outlet_N + outlet_return signals every tick.
        // When kgToDeliver = 0 (transit delay, no upstream product) all outlets = 0.
        // 'out' handle (no number) maps to outlet_1 to match getEdgeSignalKey logic.
        {
            const elevStOut = simStates[elevator.id];
            const activeTarget = kgToDeliver > 0
                ? (mainActiveTargetId ?? elevStOut?.currentTargetId)
                : null;
            const elevLv = newLiveValuesMap[elevator.id];

            // DEBUG: trace elevator (remove after diagnosis)
            if (_dbg && elevLv) {
                const upStr = upstreamContainers.map(u => `${u.id}:${(u.st?.levelKg??0).toFixed(0)}kg`).join(' ');
                const transitQ = simState.godetTransitQueue || [];
                const tSum = transitQ.reduce((s,v)=>s+v,0);
                const outKeys = Object.keys(elevLv).filter(k => k.startsWith('outlet_')).map(k => `${k}=${elevLv[k]}`).join(' ');
                // Show ALL edges from elevator with handles to diagnose missing targets
                const allElevEdges = edges.filter(e => e.source === elevator.id);
                const edgeStr = allElevEdges.map(e => `${e.sourceHandle}→${e.target}(${nodeMap[e.target]?.type??'?'})`).join(' ');
                const downStr = downstreamNodes.map(n => {
                    const s = simStates[n.id];
                    const cap = parseFloat(n.data?.config?.sim?.sim_capacity_kg ?? 0);
                    return `${n.id}(${n.type.substring(0,6)}) ${(s?.levelKg??0).toFixed(0)}/${cap}kg`;
                }).join(' ');
                console.log(`[ELEVATOR] id=${elevator.id} spd=${speedup} run=${running} godets=${godetCount}×${godetKg}kg rev=${revMin}min load=${kgThisTick.toFixed(1)}kg deliver=${kgToDeliver.toFixed(1)}kg target=${activeTarget} queue=[${simState.requestQueue}] transit=${transitQ.length}slots/${tSum.toFixed(0)}kg chainCap=${maxChainKg} up=[${upStr}] ${outKeys}`);
                console.log(`[ELEVATOR EDGES] ${edgeStr}`);
                console.log(`[ELEVATOR DOWNSTREAM] ${downStr}`);
            }
            if (elevLv) {
                const outEdges = edges.filter(
                    e => e.source === elevator.id && /^out\d*$/.test(e.sourceHandle)
                );
                for (const oe of outEdges) {
                    // Resolve passthrough to the first downstream container.
                    let targetId = oe.target;
                    const midNode = nodeMap[oe.target];
                    if (midNode && !CONTAINER_TYPES.has(midNode.type)) {
                        for (const de of edges.filter(e2 => e2.source === oe.target)) {
                            if (CONTAINER_TYPES.has(nodeMap[de.target]?.type)) {
                                targetId = de.target; break;
                            }
                        }
                    }
                    const outIdx = oe.sourceHandle.replace('out', '') || '1'; // 'out'→'1', 'out2'→'2'
                    // Single outlet: only ONE gate open at a time (the active target)
                    elevLv[`outlet_${outIdx}`] = (kgToDeliver > 0 && targetId === mainActiveTargetId) ? 1 : 0;
                }
                elevLv.outlet_return = returnDelivered ? 1 : 0;
            }
        }
    });

    // ── Cooler discharge → Granulator → Tolva 900 kg transfer ───────────────
    // tickNode drains the cooler's levelKg directly; here we transfer those kg
    // into the downstream buffer hoppers (via granulator pass-throughs) so the
    // 900 kg tolvas actually fill up and feed the bucket elevator.
    allNodes.filter(n => n.type === 'cooler_blender').forEach(cooler => {
        const simState = simStates[cooler.id];
        if (!simState || !(simState.coolerDrainedKgThisTick > 0)) return;
        const drainKg = simState.coolerDrainedKgThisTick;
        simState.coolerDrainedKgThisTick = 0; // consume — prevent double-counting

        // Collect unique destination containers, treating granulators as pass-throughs.
        // Coolers often have duplicate edges (out + out1) to the same granulator — deduplicate.
        const seenIds = new Set();
        const targets = [];
        for (const edge of edges.filter(e => e.source === cooler.id && e.sourceHandle !== 'air_out')) {
            const mid = nodeMap[edge.target];
            if (!mid) continue;
            if (CONTAINER_TYPES.has(mid.type)) {
                if (!seenIds.has(mid.id)) { seenIds.add(mid.id); targets.push(mid); }
            } else {
                // Passthrough (granulator) → collect ALL downstream containers
                let foundTarget = false;
                for (const de of edges.filter(e2 => e2.source === mid.id)) {
                    const dn = nodeMap[de.target];
                    if (dn && CONTAINER_TYPES.has(dn.type) && !seenIds.has(dn.id)) {
                        seenIds.add(dn.id); targets.push(dn); foundTarget = true;
                        if (_dbg) console.log(`[COOLER→TOLVA] cooler=${cooler.id} passthrough=${mid.id}(${mid.type}) → container=${dn.id}(${dn.type})`);
                    }
                }
                if (!foundTarget && _dbg) console.warn(`[COOLER→TOLVA] cooler=${cooler.id} passthrough=${mid.id}(${mid.type}) has NO downstream container!`);
            }
        }
        if (targets.length === 0) {
            console.warn(`[COOLER→TOLVA] cooler=${cooler.id} drainKg=${drainKg.toFixed(1)} but NO downstream container found! Check edges.`);
            return;
        }

        const kgPerTarget = drainKg / targets.length;
        for (const target of targets) {
            const tSt = simStates[target.id];
            const tLv = newLiveValuesMap[target.id];
            if (!tSt || !tLv) continue;
            const beforeKg = tSt.levelKg ?? 0;
            syncContainerLevel(target, tSt, tLv, kgPerTarget,
                parseInt(target.data?.config?.level_cap_count ?? 0));
            // Post-delivery sensor: mark full tolvas as not requesting
            const tSimCfg = target.data?.config?.sim || {};
            const tCap = parseFloat(tSimCfg.sim_capacity_kg ?? 0);
            const tCapLvls = tSimCfg.sim_cap_levels;
            const tSensorCnt = parseInt(target.data?.config?.level_cap_count ?? 0);
            if (tCap > 0 && tSensorCnt > 0 && tSt.levelKg != null) {
                const tPct = (tSt.levelKg / tCap) * 100;
                const offIdx = Math.min(parseInt(tSimCfg.sim_request_off_sensor ?? Math.max(tSensorCnt - 2, 0)), tSensorCnt - 1);
                const offThr = Array.isArray(tCapLvls) && offIdx < tCapLvls.length
                    ? parseFloat(tCapLvls[offIdx]) : 90;
                if (tPct >= offThr) {
                    tSt.containerRequesting = false;
                    tLv.requesting_product = 0;
                }
            }
            if (_dbg) console.log(`[COOLER→TOLVA] cooler=${cooler.id} phase=${simState.coolerPhase} drainKg=${drainKg.toFixed(1)} → target=${target.id}(${target.type}) +${kgPerTarget.toFixed(1)}kg ${beforeKg.toFixed(1)}→${(tSt.levelKg??0).toFixed(1)}kg`);
        }
    });

    // ── Container → Packager material transfer ──────────────────────────────
    // Handles intermediate hoppers (hopper_screw) that sit between an elevator and a packager.
    // When the hopper is running and has material, drain it into the downstream packager buffer.
    allNodes.forEach(node => {
        if (!CONTAINER_TYPES.has(node.type)) return;
        const simState = simStates[node.id];
        if (!simState) return;
        const lv = newLiveValuesMap[node.id];
        if (!lv) return;
        const running = parseFloat(lv.state ?? 0) >= 0.5;
        if (_dbg && elevatorTouchedIds.has(node.id)) {
            const simCfg_ = node.data?.config?.sim || {};
            const cap_ = parseFloat(simCfg_.sim_capacity_kg) || 1000;
            console.log(`[HOPPER-PRE] id=${node.id} levelKg=${simState.levelKg} rampValue=${(simState.rampValue??0).toFixed(1)} cap=${cap_} running=${running} state=${lv.state}`);
        }
        if (!running) return;

        const downPackagers = edges
            .filter(e => e.source === node.id)
            .map(e => allNodes.find(n => n.id === e.target))
            .filter(n => n && PACKAGER_TYPES.has(n.type));
        if (downPackagers.length === 0) return;

        const simCfg = node.data?.config?.sim || {};
        const cap = parseFloat(simCfg.sim_capacity_kg) || 1000;
        if (simState.levelKg == null) simState.levelKg = simState.rampValue * cap / 100;
        if (simState.levelKg <= 0) {
            // Hopper is empty — clear packager buffers so machines stop immediately
            for (const packager of downPackagers) {
                const pst = simStates[packager.id];
                if (!pst) continue;
                pst.packagerBufferKg = 0;
                pst._hasCapacityHopper = true;
            }
            return;
        }

        let anyPackagerRunning = false;
        const hopperSimCfg = node.data?.config?.sim || {};
        const screwRateKgMin = parseFloat(hopperSimCfg.sim_screw_rate_kg_min ?? 22.79);
        for (const packager of downPackagers) {
            const pst = simStates[packager.id];
            if (!pst) continue;
            const pCfg = packager.data?.config?.sim || {};
            const bufCap = parseFloat(pCfg.sim_packager_hopper_kg ?? 10);
            if (pst.packagerBufferKg == null) pst.packagerBufferKg = 0;

            // If the downstream packager is OFF, don't drain the hopper.
            const pkgLv = newLiveValuesMap[packager.id] || {};
            const packagerRunning = parseFloat(pkgLv.state ?? 0) >= 0.5;
            if (!packagerRunning) {
                pst.packagerBufferKg = 0;   // machine OFF → empty buffer
                pst._hasCapacityHopper = true;
                continue;
            }
            anyPackagerRunning = true;

            // Screw only turns when the packager is requesting product.
            // Use previous tick's requesting_product (current tick hasn't been computed yet).
            const prevPkgLv = packager.data?.liveValues || {};
            const pkgRequesting = parseFloat(prevPkgLv.requesting_product ?? 1) >= 0.5;
            if (pkgRequesting && simState.levelKg > 0) {
                // Screw ON: transfer from hopper to machine buffer at screw rate
                const screwKgPerTick = screwRateKgMin * simMinPerTick;
                const spaceInBuffer = Math.max(0, bufCap - pst.packagerBufferKg);
                const transferKg = Math.min(simState.levelKg, screwKgPerTick, spaceInBuffer);
                if (transferKg > 0) {
                    syncContainerLevel(node, simState, lv, -transferKg, parseInt(node.data?.config?.level_cap_count ?? 0));
                    pst.packagerBufferKg = Math.min(bufCap, pst.packagerBufferKg + transferKg);
                }
                if (_dbg) console.log(`[HOPPER→PKG] hopper=${node.id} →pkg=${packager.id} SCREW ON +${transferKg.toFixed(1)}kg hopperKg=${(simState.levelKg??0).toFixed(1)}kg pkgBuf=${pst.packagerBufferKg.toFixed(1)}/${bufCap}kg screwRate=${screwRateKgMin}kg/min`);
            } else {
                if (_dbg) console.log(`[HOPPER→PKG] hopper=${node.id} →pkg=${packager.id} SCREW OFF req=${pkgRequesting?1:0} hopperKg=${(simState.levelKg??0).toFixed(1)}kg pkgBuf=${pst.packagerBufferKg.toFixed(1)}/${bufCap}kg`);
            }
            pst._hasCapacityHopper = true;
        }

        // Post-drain sensor update: if hopper level dropped below ON threshold,
        // immediately set containerRequesting=true so elevator fills it next tick.
        // (Fixes tickNode running BEFORE material flows — edge detection misses fast drains.)
        const nSimCfg    = node.data?.config?.sim || {};
        const nCap       = parseFloat(nSimCfg.sim_capacity_kg ?? 0);
        const nCapLvls   = nSimCfg.sim_cap_levels;
        const nSensorCnt = parseInt(node.data?.config?.level_cap_count ?? 0);
        if (nCap > 0 && nSensorCnt > 0 && simState.levelKg != null) {
            const postDrainPct = (simState.levelKg / nCap) * 100;
            const onIdx = Math.min(parseInt(nSimCfg.sim_request_on_sensor ?? 0), nSensorCnt - 1);
            const onThr = Array.isArray(nCapLvls) && onIdx < nCapLvls.length
                ? parseFloat(nCapLvls[onIdx]) : [33, 67, 90][onIdx] ?? 67;
            if (postDrainPct < onThr && !simState.containerRequesting) {
                simState.containerRequesting = true;
                lv.requesting_product = 1;
            }
        }

        // If every downstream packager is OFF, mark this hopper as OFF too.
        // This turns the flow line grey and stops the elevator from filling it.
        if (!anyPackagerRunning) lv.state = 0;
    });

    // ── Apply cap sensors for standalone containers (not elevator-driven) ───
    allNodes.forEach(node => {
        if (!CONTAINER_TYPES.has(node.type)) return;
        const capCount = parseInt(node.data?.config?.level_cap_count ?? 0);
        if (capCount === 0) return;
        if (elevatorTouchedIds.has(node.id)) return; // already handled above
        const lv = newLiveValuesMap[node.id];
        if (!lv) return;
        const simCfg = node.data?.config?.sim || {};
        const cap = parseFloat(simCfg.sim_capacity_kg ?? 0);
        const st  = simStates[node.id];
        let levelPct = lv.level ?? 0;
        if (cap > 0 && st) {
            // Sync levelKg from rampValue on first encounter
            if (st.levelKg == null) st.levelKg = st.rampValue * cap / 100;
            levelPct = Math.min(100, Math.max(0, (st.levelKg / cap) * 100));
        }
        applyCapSensors(simCfg, levelPct, lv, capCount);
    });

    // ── Packager buffer drain + requesting_product override ─────────────────
    allNodes.forEach(node => {
        if (!PACKAGER_TYPES.has(node.type)) return;
        const st = simStates[node.id];
        if (!st || st.packagerBufferKg == null) return;

        const cfg      = node.data?.config?.sim || {};
        const topCfg   = node.data?.config || {};
        const bufCap       = parseFloat(cfg.sim_packager_hopper_kg ?? 10);
        const nominalSpeed = parseFloat(cfg.sim_nominal_speed ?? 0);   // packs/min per lane
        const unitWeightKg = parseFloat(cfg.sim_unit_weight_g ?? 0) / 1000;
        const lanes        = parseFloat(topCfg.lanes ?? 1);
        const lv           = newLiveValuesMap[node.id] || {};
        const running      = parseFloat(lv.state ?? 0) >= 0.5;

        // Drain buffer: machine consumes product from its own internal hopper (packagerBufferKg).
        // The buffer is filled by the screw (hopper→packager section) or by the elevator directly.
        const consumeRateOverride = parseFloat(cfg.sim_consumption_rate_kg_min ?? 0);
        const consumeKgPerSimMin = consumeRateOverride > 0
            ? consumeRateOverride
            : (nominalSpeed > 0 && unitWeightKg > 0 ? unitWeightKg * lanes * nominalSpeed : 0);
        if (running && consumeKgPerSimMin > 0) {
            st.packagerBufferKg = Math.max(0, st.packagerBufferKg - consumeKgPerSimMin * simMinPerTick);
        }
        const hadHopper = !!st._hasCapacityHopper;
        st._hasCapacityHopper = false; // clear flag each tick

        // Hysteresis-based requesting_product driven by buffer level.
        // sim_request_on_pct: buffer % at which machine starts requesting (default 30)
        // sim_request_off_pct: buffer % at which machine stops requesting (default 90)
        const reqOnPct  = parseFloat(cfg.sim_request_on_pct ?? 30);
        const reqOffPct = parseFloat(cfg.sim_request_off_pct ?? 90);
        const bufPct = bufCap > 0 ? (st.packagerBufferKg / bufCap) * 100 : 0;
        let req = st.currentBoolValues.requesting_product;
        if (req == null) req = bufPct < reqOnPct ? 1 : 0; // first-tick init
        if (bufPct <= reqOnPct) req = 1;
        if (bufPct >= reqOffPct) req = 0;
        st.currentBoolValues.requesting_product = req;

        if (newLiveValuesMap[node.id]) {
            newLiveValuesMap[node.id].requesting_product = running ? req : 0;
            newLiveValuesMap[node.id]._packagerBufferEmpty = (st.packagerBufferKg <= 0);
        }
        if (_dbg) console.log(`[PACKAGER] id=${node.id} run=${running} buf=${st.packagerBufferKg.toFixed(1)}/${bufCap}kg (${bufPct.toFixed(0)}%) req=${req} consume=${consumeKgPerSimMin.toFixed(2)}kg/min hopper=${hadHopper} onPct=${reqOnPct} offPct=${reqOffPct}`);
    });
}

// ── Tier 2: Per-node value generation ─────────────────────────────────────

function tickNode(ctx) {
    const { node, nodeId, elementType, config, simState, signalKeys, flowFraction, speedup, outEdgeMap, newLiveValuesMap, nodeMap, allNodes, allEdges } = ctx;
    const { phase, tick } = simState;
    const lv = {};
    const noise = (scale) => (Math.random() - 0.5) * 2 * scale;

    // ── State (ON/OFF) toggle ──────────────────────────────────────────────
    const simCfg = config.sim || {};
    let stateOn = true;
    if (signalKeys.includes('state')) {
        if (simState.frozenSignals.has('state')) {
            stateOn = (simState.currentBoolValues.state ?? 1) >= 1;
        } else if (simState.tempInterlockBlocked) {
            // Temperature interlock: downstream cooler has not reached setpoint yet.
            // Hold ribbon_blender OFF and reset cycle timer so it starts fresh when unblocked.
            simState.currentBoolValues.state = 0;
            simState.boolTimers.state = null;
            lv.state = 0;
            simState.tick++;
            return lv;
        } else {
            // Feature 1: Startup offset — hold state=0 until elapsed sim time passes
            if (simState.startupOffsetRemaining === undefined) {
                const offsetS = parseFloat(simCfg.sim_startup_offset_s ?? 0);
                simState.startupOffsetRemaining = offsetS > 0 ? offsetS : 0;
            }
            if (simState.startupOffsetRemaining > 0) {
                simState.startupOffsetRemaining -= TICK_DURATION_S * speedup;
                simState.currentBoolValues.state = 0;
                lv.state = 0;
                simState.tick++;
                return lv; // all other signals stay 0 during offset
            }

            // Feature 2: Always on — never cycle off
            if (simCfg.sim_always_on) {
                simState.currentBoolValues.state = 1;
                stateOn = true;
            } else if (simCfg.sim_run_on_downstream_signal || simCfg.sim_open_on_downstream_signal) {
                // Feature 4: Demand-driven — state is set by the graph-level pass each tick.
                // Skip cycle timer entirely; just read the value written by the previous tick's pass.
                stateOn = (simState.currentBoolValues.state ?? 0) === 1;
            } else if (simCfg.sim_triggers?.length > 0) {
                // Trigger-controlled — normal cycle timer disabled. The trigger system is the
                // sole controller of state. Holding current value until next trigger fires.
                // Without this, the boolTimer would override trigger-set states after sim_cycle_on ticks.
                // Safety: explicitly initialize to OFF on first tick so the node doesn't start ON.
                if (simState.currentBoolValues.state === undefined)
                    simState.currentBoolValues.state = 0;
                stateOn = simState.currentBoolValues.state === 1;
            } else {
                const defaults = ELEMENT_SIM_DEFAULTS[elementType] || ELEMENT_SIM_DEFAULTS._default;
                const onSeconds  = simCfg.sim_cycle_on  ?? defaults.sim_cycle_on;
                const offSeconds = simCfg.sim_cycle_off ?? defaults.sim_cycle_off;
                // boolTimers.state stores simulated seconds remaining (speedup-independent)
                const simSecsPerTick = TICK_DURATION_S * speedup;

                if (simState.boolTimers.state == null) {
                    simState.boolTimers.state = onSeconds;
                    simState.currentBoolValues.state = 1;
                }
                simState.boolTimers.state -= simSecsPerTick;
                if (simState.boolTimers.state <= 0) {
                    const wasOn = simState.currentBoolValues.state === 1;
                    simState.currentBoolValues.state = wasOn ? 0 : 1;
                    simState.boolTimers.state = wasOn ? offSeconds : onSeconds;
                }
                stateOn = simState.currentBoolValues.state === 1;
            }
        }
        // I2: Overload trip keeps equipment OFF until manually reset (toggle state in UI)
        if (simState.tripped) {
            if (simState.frozenSignals.has('state') && stateOn) {
                // Manual override: operator acknowledged and reset
                simState.tripped = false;
            } else {
                stateOn = false;
            }
        }
        lv.state = stateOn ? 1 : 0;
    }

    // ── 5.3: CIP (Clean-in-Place) ─────────────────────────────────────────
    // Equipment with sim_cip_interval_s periodically enters a cleaning cycle.
    // During CIP: equipment stays ON (motor pumps cleaning fluid) but production is blocked.
    // Manual trigger: freeze 'cip_active' signal to 1 to force CIP start.
    const cipIntervalS = parseFloat(simCfg.sim_cip_interval_s ?? 0);
    const cipDurationS = parseFloat(simCfg.sim_cip_duration_s ?? 0);
    let cipActive = false;
    if (cipDurationS > 0) {
        if (simState.cipActive === undefined) simState.cipActive = false;
        if (simState.cipTimer === undefined) simState.cipTimer = 0;
        if (simState.cipProductionTimer === undefined) simState.cipProductionTimer = 0;

        const simSecsThisTick = TICK_DURATION_S * speedup;

        // Manual CIP trigger via frozen signal
        if (simState.frozenSignals.has('cip_active')) {
            const manualCip = (simState.currentBoolValues.cip_active ?? 0) >= 1;
            if (manualCip && !simState.cipActive) {
                simState.cipActive = true;
                simState.cipTimer = cipDurationS;
            }
        }

        if (simState.cipActive) {
            // CIP in progress — count down
            simState.cipTimer -= simSecsThisTick;
            if (simState.cipTimer <= 0) {
                simState.cipActive = false;
                simState.cipTimer = 0;
                simState.cipProductionTimer = 0; // reset interval counter
                // Unfreeze manual trigger if it was used
                if (simState.frozenSignals.has('cip_active')) {
                    simState.currentBoolValues.cip_active = 0;
                }
            }
            cipActive = true;
        } else if (cipIntervalS > 0 && stateOn) {
            // Count production time toward next CIP
            simState.cipProductionTimer += simSecsThisTick;
            if (simState.cipProductionTimer >= cipIntervalS) {
                simState.cipActive = true;
                simState.cipTimer = cipDurationS;
                cipActive = true;
            }
        }
    }
    lv.cip_active = cipActive ? 1 : 0;

    // running = equipment is ON and NOT in CIP (available for production)
    const running = stateOn && !cipActive;
    const bases = BASES[elementType] || {};

    // Startup inrush detection (state 0→1 rising edge)
    const stateJustStarted = running && simState.prevState === 0;
    if (stateJustStarted) {
        simState.startupInrushTicks = 2 + Math.floor(Math.random() * 2);
    }
    simState.prevState = running ? 1 : 0;
    if (!running) simState.startupInrushTicks = 0;
    const inInrush = simState.startupInrushTicks > 0;
    if (inInrush) simState.startupInrushTicks--;

    // Elapsed runtime tracking (for hover panel display)
    if (running) {
        simState.runningForTicks = (simState.runningForTicks ?? 0) + 1;
    } else {
        simState.runningForTicks = 0;
    }
    if (signalKeys.includes('state')) {
        lv.runtime_s = running
            ? Math.round(simState.runningForTicks * TICK_DURATION_S * speedup)
            : 0;
    }

    // ── CIP Skid: dedicated state machine (early return) ─────────────────
    // The CIP skid has its own phase cycle independent of the generic signal loop.
    // Phases: 0=idle, 1=pre-rinse, 2=caustic, 3=inter-rinse, 4=acid, 5=final-rinse, 6=drain
    if (elementType === 'cip_skid') {
        const simSecsPerTick = TICK_DURATION_S * speedup;
        const phaseDurations = [
            0,                                                                      // 0: idle (no timer)
            parseFloat(simCfg.sim_rinse_duration_s ?? 120),                         // 1: pre-rinse
            parseFloat(simCfg.sim_caustic_duration_s ?? 600),                       // 2: caustic wash
            parseFloat(simCfg.sim_rinse_duration_s ?? 120),                         // 3: inter-rinse
            parseFloat(simCfg.sim_acid_duration_s ?? 300),                          // 4: acid wash
            parseFloat(simCfg.sim_rinse_duration_s ?? 120),                         // 5: final rinse
            parseFloat(simCfg.sim_drain_duration_s ?? 60),                          // 6: drain
        ];
        if (simState.cipSkidPhase === undefined) simState.cipSkidPhase = 0;
        if (simState.cipSkidTimer === undefined) simState.cipSkidTimer = 0;
        // Tank levels (start full)
        if (simState.cipWaterLevel === undefined) simState.cipWaterLevel = 80;
        if (simState.cipCausticLevel === undefined) simState.cipCausticLevel = 80;
        if (simState.cipAcidLevel === undefined) simState.cipAcidLevel = 80;
        // Temp starts at ambient
        if (simState.cipTemp === undefined) simState.cipTemp = 30;

        const cipPhase = simState.cipSkidPhase;

        if (stateOn && cipPhase === 0) {
            // Start CIP cycle
            simState.cipSkidPhase = 1;
            simState.cipSkidTimer = phaseDurations[1];
        } else if (cipPhase > 0) {
            simState.cipSkidTimer -= simSecsPerTick;
            if (simState.cipSkidTimer <= 0) {
                const nextPhase = cipPhase + 1;
                if (nextPhase > 6) {
                    // Cycle complete — return to idle, turn off
                    simState.cipSkidPhase = 0;
                    simState.cipSkidTimer = 0;
                    simState.currentBoolValues.state = 0;
                    lv.state = 0;
                } else {
                    simState.cipSkidPhase = nextPhase;
                    simState.cipSkidTimer = phaseDurations[nextPhase];
                }
            }

            // Tank level dynamics per phase
            const drainRate = 30 * simSecsPerTick / Math.max(1, phaseDurations[cipPhase] || 120);
            const refillRate = drainRate * 0.8; // tanks refill slower than they drain
            switch (cipPhase) {
                case 1: // pre-rinse: water drains
                case 3: // inter-rinse: water drains
                case 5: // final rinse: water drains
                    simState.cipWaterLevel = Math.max(20, simState.cipWaterLevel - drainRate);
                    break;
                case 2: // caustic: caustic drains, water refills
                    simState.cipCausticLevel = Math.max(20, simState.cipCausticLevel - drainRate);
                    simState.cipWaterLevel = Math.min(100, simState.cipWaterLevel + refillRate);
                    break;
                case 4: // acid: acid drains, water refills
                    simState.cipAcidLevel = Math.max(20, simState.cipAcidLevel - drainRate);
                    simState.cipWaterLevel = Math.min(100, simState.cipWaterLevel + refillRate);
                    break;
                case 6: // drain: all levels slowly recover (refill from supply)
                    simState.cipWaterLevel = Math.min(100, simState.cipWaterLevel + refillRate);
                    simState.cipCausticLevel = Math.min(100, simState.cipCausticLevel + refillRate * 0.5);
                    simState.cipAcidLevel = Math.min(100, simState.cipAcidLevel + refillRate * 0.5);
                    break;
            }

            // Temperature: caustic wash is hot (~80°C), acid is warm (~45°C), rinse is cool
            const tempTargets = [30, 25, 80, 25, 45, 25, 30];
            const tauT = 1 - Math.exp(-simSecsPerTick / 30);
            simState.cipTemp += (tempTargets[cipPhase] - simState.cipTemp) * tauT;
        } else {
            // Idle: levels slowly recover to 80%
            simState.cipWaterLevel += (80 - simState.cipWaterLevel) * 0.01;
            simState.cipCausticLevel += (80 - simState.cipCausticLevel) * 0.01;
            simState.cipAcidLevel += (80 - simState.cipAcidLevel) * 0.01;
            simState.cipTemp += (30 - simState.cipTemp) * 0.02;
        }

        lv.cip_phase = simState.cipSkidPhase;
        lv.pump_state = cipPhase > 0 && cipPhase < 6 ? 1 : 0;
        lv.level_water = simState.cipWaterLevel;
        lv.level_caustic = simState.cipCausticLevel;
        lv.level_acid = simState.cipAcidLevel;
        lv.temp = simState.cipTemp + noise(0.3);
        simState.tick++;
        return lv;
    }

    // ── P7: Speed ramp — exponential approach to target ───────────────────
    // Heavy equipment (blenders, elevators) ramps over ~5s real; light equipment ~2s.
    const heavyEquip = ['ribbon_blender', 'cooler_blender', 'bucket_elevator', 'granulator'].includes(elementType);
    const rampTauS = heavyEquip ? 5 : 2; // time constant in real (sim) seconds
    const rampAlpha = 1 - Math.exp(-(TICK_DURATION_S * speedup) / Math.max(0.1, rampTauS));
    const targetSpeedFrac = running ? 1 : 0;
    if (simState.speedFraction == null) simState.speedFraction = targetSpeedFrac;
    simState.speedFraction += (targetSpeedFrac - simState.speedFraction) * rampAlpha;
    // Snap to target when close enough to avoid perpetual creep
    if (Math.abs(simState.speedFraction - targetSpeedFrac) < 0.005) simState.speedFraction = targetSpeedFrac;
    const speedFrac = simState.speedFraction;

    // ── Per-signal generation ──────────────────────────────────────────────
    for (const key of signalKeys) {
        if (key === 'state') continue;

        switch (key) {
            case 'speed': {
                const isPackager = PACKAGER_TYPES.has(elementType);
                const packagerHasMaterial = !isPackager
                    || (simState.packagerBufferKg != null && simState.packagerBufferKg > 0);
                // Packagers: speed = golpes/min (strokes/min) = sim_nominal_speed.
                // BASES has no entry for packager types → would fall back to 1000 (wrong).
                const baseSpeed = isPackager
                    ? parseFloat(config.sim?.sim_nominal_speed ?? 0)
                    : (bases.speed || 1000);
                lv.speed = (packagerHasMaterial && baseSpeed > 0)
                    ? baseSpeed * speedFrac * (1 + Math.sin(tick / 8 + phase) * 0.01 + noise(0.005))
                    : 0;
                break;
            }

            case 'current': {
                if (!running) { lv.current = 0; break; }
                const baseI = bases.current || 10;
                const speedVal = lv.speed != null ? lv.speed : (bases.speed || 1000);
                const speedRatio = speedVal / (bases.speed || 1000);

                // Load from material level (blenders fill up while running)
                const loadFromLevel = ['ribbon_blender', 'cooler_blender', 'hopper_screw'].includes(elementType)
                    ? (0.6 + 0.4 * (simState.rampValue / 100))
                    : 1;

                // Flow-conductance factor: centrifugal = low current at shutoff; conveyors = high when blocked
                // Blenders are NOT centrifugal — their current depends on material mass, not flow.
                const centrifugal = ['pump', 'cyclone'].includes(elementType);
                const conveyor    = ['screw_conveyor', 'bucket_elevator', 'hopper_screw'].includes(elementType);
                const flowFactor  = centrifugal ? (0.3 + 0.7 * flowFraction)
                    : conveyor ? (0.7 + 0.3 * (1 - flowFraction))
                    : 1;

                // VFD: current ∝ speed^1.5
                const speedFactor = ['ribbon_blender', 'cooler_blender', 'cyclone', 'bucket_elevator', 'screw_conveyor'].includes(elementType)
                    ? Math.pow(speedRatio, 1.5)
                    : 1;

                // Inrush: 1.8–3.6× rated for 2–3 ticks on heavy-load startup
                const inrushFactor = inInrush ? (1.8 + loadFromLevel * 1.2 + noise(0.2)) : 1;

                lv.current = baseI * flowFactor * loadFromLevel * speedFactor * inrushFactor * speedFrac
                    * (1 + Math.sin(tick / 10 + phase) * 0.08 + noise(0.02));
                break;
            }

            case 'pressure': {
                if (!running) { lv.pressure = 0; break; }
                if (elementType === 'boiler') {
                    // 5.4: Boiler pressure proportional to temperature (steam table approximation)
                    // At ambient (~30°C) → 0 bar gauge, at target (~180°C) → rated pressure (~8 bar)
                    const boilerTarget = parseFloat(config.sim?.sim_boiler_target ?? bases.temp ?? 180);
                    const currentTemp = simState.boilerTemp ?? 30;
                    const tempFrac = Math.max(0, Math.min(1, (currentTemp - 30) / Math.max(10, boilerTarget - 30)));
                    const ratedP = bases.pressure || 8;
                    lv.pressure = ratedP * tempFrac * speedFrac * (1 + Math.sin(tick / 10 + phase) * 0.03 + noise(0.02));
                } else {
                    // Pump/compressor: pressure rises when downstream is closed
                    const pBase = bases.pressure || 6;
                    // Quadratic pump curve: pressure rises sharply near shutoff (max ~2.5× rated)
                    const boost = Math.min(2.0, 1 + Math.pow(1 - flowFraction, 2) * 1.5);
                    lv.pressure = pBase * boost * speedFrac * (1 + Math.sin(tick / 6 + phase) * 0.05 + noise(0.02));
                }
                break;
            }

            case 'level': {
                const isBlender = ['ribbon_blender', 'cooler_blender', 'hopper_screw', 'screw_conveyor'].includes(elementType);
                if (isBlender) {
                    // gate_1 (ribbon_blender) and outlet_state (cooler_blender) are the sole
                    // owners of rampValue for batch machines. The level case must NOT also
                    // modify it — doing so fights the gate logic and breaks fill timing.
                    // hopper_screw / screw_conveyor has neither gate signal, so it uses flow-based fill/drain
                    // when there is no sim_capacity_kg. When sim_capacity_kg IS set, the level is
                    // managed exclusively by computeMaterialFlows (elevator delivery/drain).
                    // Applying an auto-fill rate here would inflate the level even when no material
                    // has arrived, so we skip it for capacity-configured containers.
                    if (!signalKeys.includes('gate_1') && !signalKeys.includes('outlet_state')
                            && !parseFloat(simCfg.sim_capacity_kg ?? 0)) {
                        const fillRate  = running ? 0.5 : 0;
                        const drainRate = flowFraction > 0.5 ? 1.5 : 0;
                        simState.rampValue = Math.min(100, Math.max(0, simState.rampValue + fillRate - drainRate));
                    }
                } else if (simCfg.sim_consumption_mode === 'estimated') {
                    // Feature 6: Estimated consumption — level managed externally; just emit it
                    // (applyEstimatedConsumption pass will update levelKg/rampValue)
                } else {
                    // Tank/silo: sim_tank_refill=false → only drains (no automatic refill)
                    const refills = simCfg.sim_tank_refill !== false;
                    if (refills) {
                        simState.rampValue += simState.rampDir * (0.8 + flowFraction * 0.4);
                        if (simState.rampValue >= 95) simState.rampDir = -1;
                        if (simState.rampValue <= 5)  simState.rampDir =  1;
                    } else {
                        // Drain at a steady rate proportional to downstream flow, stop at 0
                        simState.rampValue = Math.max(0, simState.rampValue - 0.3 * (0.5 + flowFraction));
                    }
                }
                // D2+D3: derive level from levelKg when available; rampValue is fallback
                const _capForLevel = parseFloat(simCfg.sim_capacity_kg ?? 0) || parseFloat(simCfg.sim_batch_capacity_kg ?? 0);
                if (simState.levelKg != null && _capForLevel > 0) {
                    simState.rampValue = Math.min(100, (simState.levelKg / _capForLevel) * 100);
                }
                lv.level = simState.rampValue;
                // Feature 7a: Emit at_capacity signal when container is full
                if (simCfg.sim_emit_at_capacity) {
                    lv.at_capacity = simState.rampValue >= 99 ? 1 : 0;
                }
                break;
            }

            case 'temp_supply': {
                // Chiller: exponential approach from ambient (30°C) toward target when ON,
                // and from target toward ambient when OFF.
                const ambientTemp = 30;
                const targetTemp = parseFloat(config.sim?.sim_chiller_target ?? 6);
                if (simState.chillerTemp === undefined) simState.chillerTemp = ambientTemp;
                // tau scales with speedup so simulated cooling time is constant across speedup values.
                // Base 0.025 calibrated at speedup=60; formula: 1 - (1-base)^(speedup/60)
                const tau = 1 - Math.pow(0.975, speedup / 60);
                const goal = running ? targetTemp : ambientTemp;
                simState.chillerTemp += (goal - simState.chillerTemp) * tau;
                lv.temp_supply = simState.chillerTemp + noise(0.15);
                break;
            }

            case 'temp_return': {
                // 5.2: Return temp rises when chiller is absorbing heat from cooler batch
                const thermalLoad = simState.chillerThermalLoad ?? 0;
                const baseReturnOffset = 5; // nominal delta above supply
                const loadBoost = thermalLoad * 8; // up to +8°C when fully loaded
                lv.temp_return = (lv.temp_supply ?? 7) + baseReturnOffset + loadBoost
                    + Math.sin(tick / 35 + phase + 1) * 1 + noise(0.2);
                break;
            }

            case 'temp': {
                if (elementType === 'cooler_blender') {
                    // P2: Cooler temperature follows batch phases (monitoring only).
                    // - idle: ambient (no product)
                    // - filling: jumps toward hot batch temp (~55°C) as product arrives
                    // - holding: exponential decay toward chiller target (~8°C)
                    // - discharging: holds at current temp (product already cooled)
                    const ambientTemp = 30;
                    const hotBatchTemp = simState.hotBatchTemp ?? 55;
                    const targetTemp = parseFloat(config.sim?.sim_cooler_target ?? 8);
                    if (simState.coolerTemp === undefined) simState.coolerTemp = ambientTemp;

                    // Chiller connection: if a running chiller is piped to chw_in,
                    // the cooler jacket has cold water available for cooling.
                    const chillerOn = !!simState.chillerConnected;
                    const chwTemp = simState.chillerSupplyTemp ?? 7;

                    const cPhase = simState.coolerPhase ?? 'idle';
                    switch (cPhase) {
                        case 'idle':
                            // Empty cooler: if chiller supplies cold water, vessel cools
                            // toward chiller supply temp. Otherwise drifts to ambient.
                            if (chillerOn) {
                                const idleTau = 1 - Math.exp(-(TICK_DURATION_S * speedup) / 120);
                                simState.coolerTemp += (chwTemp - simState.coolerTemp) * idleTau;
                            } else {
                                simState.coolerTemp += (ambientTemp - simState.coolerTemp) * 0.1;
                            }
                            break;
                        case 'filling': {
                            // Hot product arrives — temp rises toward hotBatchTemp weighted by fill level
                            const fillPct = simState.levelKg != null && simState.coolerBatchCapKg > 0
                                ? simState.levelKg / simState.coolerBatchCapKg : 0;
                            // Blend: vessel temp blends with incoming hot product
                            const blendTarget = simState.coolerTemp + (hotBatchTemp - simState.coolerTemp) * Math.min(1, fillPct);
                            const fillTau = 1 - Math.pow(0.90, speedup / 60);
                            simState.coolerTemp += (blendTarget - simState.coolerTemp) * fillTau;
                            break;
                        }
                        case 'holding': {
                            // Chilled water cools product: rate depends on chiller being connected.
                            // Without chiller: very slow natural cooling toward ambient.
                            // With chiller: exponential decay toward chiller supply temp.
                            const holdTimeS = parseFloat(simCfg.sim_hold_time_s ?? 1800);
                            if (chillerOn) {
                                // Time constant: holdTimeS/5 → ~99.3% convergence by end of hold
                                const tcS = holdTimeS / 5;
                                const holdTau = 1 - Math.exp(-(TICK_DURATION_S * speedup) / Math.max(1, tcS));
                                simState.coolerTemp += (chwTemp - simState.coolerTemp) * holdTau;
                            } else {
                                // No chiller: slow drift toward ambient (product barely cools)
                                const slowTau = 1 - Math.exp(-(TICK_DURATION_S * speedup) / (holdTimeS * 2));
                                simState.coolerTemp += (ambientTemp - simState.coolerTemp) * slowTau;
                            }
                            break;
                        }
                        case 'discharging':
                            // Product already cooled, temp stays roughly constant
                            break;
                    }
                    lv.temp = simState.coolerTemp + noise(0.2);
                    // temp_ready: 1 when within configured tolerance of target (default ±2°C)
                    const tolerance = parseFloat(config.sim?.sim_cooler_tolerance ?? 2);
                    lv.temp_ready = Math.abs(simState.coolerTemp - targetTemp) <= tolerance ? 1 : 0;
                } else if (elementType === 'boiler') {
                    // 5.4: Boiler temp — exponential approach to setpoint when ON, decay to ambient when OFF
                    const ambientTemp = 30;
                    const boilerTarget = parseFloat(config.sim?.sim_boiler_target ?? bases.temp ?? 180);
                    if (simState.boilerTemp === undefined) simState.boilerTemp = ambientTemp;
                    const boilerTau = 1 - Math.pow(0.98, speedup / 60);
                    const boilerGoal = running ? boilerTarget : ambientTemp;
                    simState.boilerTemp += (boilerGoal - simState.boilerTemp) * boilerTau;
                    lv.temp = simState.boilerTemp + noise(0.5);
                } else if (elementType === 'water_heater') {
                    // 5.4: Water heater temp — exponential approach to setpoint when ON
                    const ambientTemp = 25;
                    const whTarget = parseFloat(config.sim?.sim_heater_target ?? bases.temp ?? 65);
                    if (simState.heaterTemp === undefined) simState.heaterTemp = ambientTemp;
                    // Faster tau than boiler (water heats quicker than steam generation)
                    const whTau = 1 - Math.pow(0.96, speedup / 60);
                    const whGoal = running ? whTarget : ambientTemp;
                    simState.heaterTemp += (whGoal - simState.heaterTemp) * whTau;
                    lv.temp = simState.heaterTemp + noise(0.3);
                } else {
                    lv.temp = (bases.temp || 65) + Math.sin(tick / 30 + phase) * 5 + noise(0.5);
                }
                break;
            }

            // temp_ready is set as a side-effect of case 'temp' above; no-op here to avoid default: lv[key]=0
            case 'temp_ready': break;

            case 'pressure_high': {
                // 5.2: High-side pressure rises with thermal load (compressor works harder)
                const thLoad_ph = simState.chillerThermalLoad ?? 0;
                const phBase = 16 + thLoad_ph * 4; // up to +4 bar under full load
                lv.pressure_high = running
                    ? phBase + Math.sin(tick / 12 + phase) * 2 + noise(0.5)
                    : 8 + noise(0.2);
                break;
            }

            case 'pressure_low': {
                // 5.2: Low-side pressure drops slightly under load (evaporator absorbs more heat)
                const thLoad_pl = simState.chillerThermalLoad ?? 0;
                const plBase = 4.2 - thLoad_pl * 0.6; // drops up to -0.6 bar under full load
                lv.pressure_low = running
                    ? plBase + Math.sin(tick / 14 + phase + 0.5) * 0.8 + noise(0.3)
                    : 4 + noise(0.1);
                break;
            }

            case 'value': {
                const sensorFn = {
                    sensor_temp:     () => 22 + Math.sin(tick / 30 + phase) * 4 + noise(0.5),
                    sensor_pressure: () => (5 + Math.sin(tick / 20 + phase) * 2 + noise(0.3)) * flowFraction,
                    sensor_flow:     () => (120 + Math.sin(tick / 12 + phase) * 30 + noise(5)) * flowFraction,
                    sensor_level:    () => {
                        simState.rampValue += simState.rampDir * 0.8;
                        if (simState.rampValue >= 95) simState.rampDir = -1;
                        if (simState.rampValue <= 5)  simState.rampDir =  1;
                        return simState.rampValue;
                    },
                    sensor_humidity: () => 45 + Math.sin(tick / 50 + phase) * 10 + noise(1),
                }[elementType];
                lv.value = sensorFn ? sensorFn() : 0;
                break;
            }

            case 'error':
                // Alarms are manual-only in simulation — toggled via UI, not random.
                lv.error = simState.manualError ? 1 : 0;
                break;

            case 'count': {
                if (END_OF_LINE_TYPES.has(elementType)) {
                    // ── End-of-line machines: box_former, strip_folder, box_sealer ──
                    const simMinPerTick = TICK_DURATION_S * speedup / 60;

                    if (elementType === 'strip_folder') {
                        // Strip folder: count filled cartons (managed by ristras_count case)
                        lv.count = Math.round(simState._count || 0);
                    } else {
                        // box_former / box_sealer: simple throughput counter
                        const throughput = parseFloat(config.sim?.sim_throughput_cart_min ?? 30);
                        if (running && throughput > 0 && flowFraction > 0.3) {
                            simState._count = (simState._count || 0) + throughput * simMinPerTick;
                        }
                        lv.count = Math.round(simState._count || 0);
                    }
                } else {
                    // ── Packager production counter ──
                    // Uses sim_nominal_speed (golpes/min) × sim_lines_count.
                    // Requires the packager's internal buffer to have material.
                    const nominalSpeed = parseFloat(config.sim?.sim_nominal_speed ?? 0);
                    const lines = parseFloat(config.lanes ?? config.sim?.sim_lines_count ?? 1) || 1;
                    const bufferHasMaterial = simState.packagerBufferKg != null
                        && simState.packagerBufferKg > 0;
                    if (nominalSpeed > 0 && running && flowFraction > 0.5 && bufferHasMaterial) {
                        const simMinPerTick = TICK_DURATION_S * speedup / 60;
                        simState._count = (simState._count || 0) + nominalSpeed * lines * simMinPerTick;
                    }
                    lv.count = Math.round(simState._count || 0);
                }
                break;
            }

            // ── Simulation tick: requesting_product case ───────────────────────────
            case 'requesting_product': {
                const isManagedContainer = CONTAINER_TYPES.has(elementType)
                    && parseFloat(simCfg.sim_capacity_kg ?? 0) > 0;

                if (isManagedContainer) {
                    const cap      = parseFloat(simCfg.sim_capacity_kg);
                    const levelPct = simState.levelKg != null
                        ? Math.min(100, (simState.levelKg / cap) * 100)
                        : simState.rampValue;

                    const prevPct = simState._prevLevelPct ?? levelPct;

                    const onEdge  = simCfg.sim_request_on_edge  ?? 'falling';
                    const offEdge = simCfg.sim_request_off_edge ?? 'rising';

                    const capLevels   = simCfg.sim_cap_levels;
                    const sensorCount = parseInt(config.level_cap_count ?? 0);
                    const DEFAULT_LEVELS = [33, 67, 90];

                    const finalLevels = [];
                    for (let i = 0; i < sensorCount; i++) {
                        let value = Array.isArray(capLevels) && i < capLevels.length
                            ? parseFloat(capLevels[i])
                            : DEFAULT_LEVELS[i] ?? DEFAULT_LEVELS[DEFAULT_LEVELS.length - 1];

                        if (isNaN(value)) {
                            console.warn(`Nivel inválido en índice ${i}, usando fallback`);
                            value = DEFAULT_LEVELS[i] ?? DEFAULT_LEVELS[DEFAULT_LEVELS.length - 1];
                        }

                        finalLevels.push(value);
                    }

                    const safeOnIdx  = Math.min(parseInt(simCfg.sim_request_on_sensor ?? 0), finalLevels.length - 1);
                    const safeOffIdx = Math.min(parseInt(simCfg.sim_request_off_sensor ?? Math.max(sensorCount - 2, 0)), finalLevels.length - 1);

                    const onThr  = finalLevels[safeOnIdx];
                    const offThr = finalLevels[safeOffIdx];

                    // Initialize containerRequesting based on current level vs thresholds.
                    // If the container starts above the OFF threshold, it should NOT request.
                    if (simState.containerRequesting === undefined) {
                        simState.containerRequesting = offThr != null ? levelPct < offThr : true;
                    }

                    const onTriggered  = onEdge === 'falling'
                        ? (prevPct >= onThr && levelPct < onThr)
                        : (prevPct < onThr && levelPct >= onThr);

                    const offTriggered = offEdge === 'rising'
                        ? (prevPct < offThr && levelPct >= offThr)
                        : (prevPct >= offThr && levelPct < offThr);

                    if (onTriggered)  simState.containerRequesting = true;
                    if (offTriggered) simState.containerRequesting = false;

                    // Level-based guard: if above OFF threshold, don't request regardless of edges.
                    // This catches cases where level was set externally (computeMaterialFlows)
                    // and the edge was missed because tickNode didn't see the crossing.
                    if (offThr != null && levelPct >= offThr) simState.containerRequesting = false;
                    if (onThr != null && levelPct < onThr)    simState.containerRequesting = true;

                    simState._prevLevelPct = levelPct;
                    lv.requesting_product = running ? (simState.containerRequesting ? 1 : 0) : 0;

                } else {
                    // 👉 PACKAGER
                    const fillMinutes  = parseFloat(config.sim?.sim_fill_time ?? 6);
                    const fillSeconds  = fillMinutes * 60;
                    const satisfySeconds = Math.max(TICK_DURATION_S, fillSeconds * 0.2);

                    if (!simState.boolTimers.requesting_product) {
                        simState.boolTimers.requesting_product = fillSeconds;
                        simState.currentBoolValues.requesting_product = 1;
                    }

                    simState.boolTimers.requesting_product -= TICK_DURATION_S * speedup;

                    if (simState.boolTimers.requesting_product <= 0) {
                        const wasOn = simState.currentBoolValues.requesting_product === 1;
                        simState.currentBoolValues.requesting_product = wasOn ? 0 : 1;
                        simState.boolTimers.requesting_product = wasOn ? satisfySeconds : fillSeconds;
                    }

                    lv.requesting_product = running
                        ? (simState.currentBoolValues.requesting_product ?? 1)
                        : 0;
                }

                break;
            }

            case 'load_kg': {
                // Bucket elevator: total kg currently inside the chain (godetTransitQueue sum).
                // Capped at godetCount × godetKg — the physical maximum the chain can hold.
                const _maxChain = parseFloat(simCfg.sim_godet_count ?? 360) * parseFloat(simCfg.sim_godet_capacity_kg ?? 3);
                lv.load_kg = Math.min(
                    (simState.godetTransitQueue ?? []).reduce((s, k) => s + k, 0),
                    _maxChain
                );
                break;
            }

            // ── Strip folder: ristra accumulation & carton filling ──────────────
            case 'ristras_count': {
                if (elementType !== 'strip_folder') { lv.ristras_count = 0; break; }
                const ristrasPerCarton = parseInt(config.ristras_per_carton ?? 6);
                const simMinPerTick_sf = TICK_DURATION_S * speedup / 60;

                if (simState._currentRistras === undefined) simState._currentRistras = 0;

                // Check if ristras are coming in (upstream envasadora connected to ristras_in)
                const ristrasAvailable = running && flowFraction > 0.3;
                if (ristrasAvailable) {
                    // Approximate: 1 ristra per machine cycle, at ~60 cycles/min upstream
                    const ristrasPerMin = parseFloat(config.sim?.sim_ristras_per_min ?? 60);
                    simState._currentRistras += ristrasPerMin * simMinPerTick_sf;

                    // When we have enough ristras, produce a filled carton
                    while (simState._currentRistras >= ristrasPerCarton) {
                        simState._currentRistras -= ristrasPerCarton;
                        simState._count = (simState._count || 0) + 1;
                    }
                }
                lv.ristras_count = Math.floor(simState._currentRistras);
                break;
            }

            case 'carton_weight_kg': {
                if (elementType !== 'strip_folder') { lv.carton_weight_kg = 0; break; }
                const _ristras = parseInt(config.ristras_per_carton ?? 6);
                const _bolsitas = parseInt(config.bolsitas_per_ristra ?? 5);
                const _pesoG = parseFloat(config.peso_bolsita_g ?? 10);
                lv.carton_weight_kg = parseFloat((_ristras * _bolsitas * _pesoG / 1000).toFixed(3));
                break;
            }

            case 'requesting_ristras': {
                // Strip folder requests ristras when running but upstream flow is low
                if (elementType !== 'strip_folder') { lv.requesting_ristras = 0; break; }
                lv.requesting_ristras = running && flowFraction < 0.5 ? 1 : 0;
                break;
            }

            case 'requesting_carton': {
                // Strip folder requests cartons when running but no carton supply
                if (elementType !== 'strip_folder') { lv.requesting_carton = 0; break; }
                // Check if carton_in port has upstream flow: look at edges targeting this node's carton_in handle
                const cartonEdges = allEdges.filter(e => e.target === node.id && e.targetHandle === 'carton_in');
                const cartonSupplyOk = cartonEdges.length > 0 && cartonEdges.some(e => {
                    const srcNode = allNodes.find(n => n.id === e.source);
                    return srcNode && parseFloat(srcNode.data?.liveValues?.state ?? 0) >= 0.5;
                });
                lv.requesting_carton = running && !cartonSupplyOk ? 1 : 0;
                break;
            }

            // ── Belt conveyor: queue level (saturation) ──────────────────────────
            case 'queue_level': {
                if (elementType !== 'belt_conveyor') { lv.queue_level = 0; break; }
                // Compare upstream production rate vs downstream consumption rate
                // Simplified: if downstream node is stopped or has error, queue builds up
                const downEdges_q = allEdges.filter(e => e.source === node.id);
                const downNodes_q = downEdges_q.map(e => allNodes.find(n => n.id === e.target)).filter(Boolean);
                const anyDownStopped = downNodes_q.length > 0 && downNodes_q.some(dn =>
                    parseFloat(dn.data?.liveValues?.state ?? 1) < 0.5 ||
                    dn.data?.liveValues?.error === 1 || dn.data?.liveValues?.error === '1'
                );
                if (simState._queueLevel === undefined) simState._queueLevel = 0;
                if (running && anyDownStopped) {
                    simState._queueLevel = Math.min(100, simState._queueLevel + 3 * speedup);
                } else if (running) {
                    simState._queueLevel = Math.max(0, simState._queueLevel - 5 * speedup);
                } else {
                    // Belt off: queue stays
                }
                lv.queue_level = Math.round(simState._queueLevel);
                break;
            }

            case 'gate_1': {
                // gate_1 owns ALL fill/drain logic and nextGate rotation for ribbon_blender.
                // Sequential: gate_1 opens first, then gate_2, then gate_1, etc.
                if (simState.gateState  === undefined) simState.gateState  = 0;
                if (simState.gate2State === undefined) simState.gate2State = 0;
                if (simState.nextGate   === undefined) simState.nextGate   = 1;
                if (simState.frozenSignals.has('gate_1') || simState.frozenSignals.has('gate_2')) {
                    // Manual override: don't auto-advance fill/drain
                    lv.gate_1 = simState.gateState;
                    break;
                }
                const gateCount = parseInt(config.gate_count ?? 1);
                // Feature 7c: Backpressure — don't open gate if downstream is full
                const downstreamBlockedRibbon = simCfg.sim_check_downstream_capacity_before_gate
                    && simState.downstreamBlocked;
                // P1: Fill rate coupled to recipe steps — only fill during loading steps (signal != null)
                const defaults_g1 = ELEMENT_SIM_DEFAULTS[elementType] || ELEMENT_SIM_DEFAULTS._default;
                const cycleOnS_g1 = parseFloat(simCfg.sim_cycle_on ?? defaults_g1.sim_cycle_on);
                const recipeSteps_g1 = simCfg.sim_recipe_steps;
                let loadingDurationS_g1 = cycleOnS_g1; // fallback: full cycle
                let currentStepIsLoading = true;       // fallback: always loading
                if (Array.isArray(recipeSteps_g1) && recipeSteps_g1.length > 0) {
                    // Sum duration of steps that have a signal (loading material)
                    loadingDurationS_g1 = recipeSteps_g1.reduce((sum, s) =>
                        sum + (s.signal ? parseFloat(s.duration_s ?? 0) : 0), 0) || cycleOnS_g1;
                    // Check if current recipe step is a loading step
                    const curStep = recipeSteps_g1[simState.recipeStep ?? 0];
                    currentStepIsLoading = !!(curStep && curStep.signal);
                }
                // D2+D3: levelKg is single source of truth; rampValue derived.
                const batchCapG1 = parseFloat(simCfg.sim_batch_capacity_kg ?? 0);
                if (batchCapG1 > 0) {
                    // ── kg-tracked mode ─────────────────────────────────────
                    if (simState.levelKg == null) simState.levelKg = simState.rampValue * batchCapG1 / 100;
                    const fillKgPerTick = batchCapG1 / Math.max(1, cycleToTicks(loadingDurationS_g1, speedup));
                    const dischargeS_g1 = parseFloat(simCfg.sim_discharge_duration_s ?? 0);
                    const drainKgPerTick = dischargeS_g1 > 0
                        ? batchCapG1 / Math.max(1, cycleToTicks(dischargeS_g1, speedup))
                        : fillKgPerTick * 5;
                    if (simState.nextGate === 1) {
                        if (simState.gateState === 0) {
                            const canFill = running && currentStepIsLoading;
                            if (canFill) simState.levelKg = Math.min(batchCapG1, simState.levelKg + fillKgPerTick);
                            if (simState.levelKg >= batchCapG1 && running && !downstreamBlockedRibbon)
                                simState.gateState = 1;
                        } else {
                            simState.levelKg = Math.max(0, simState.levelKg - drainKgPerTick);
                            if (simState.levelKg <= 0) {
                                simState.levelKg = 0;
                                simState.gateState = 0;
                                if (gateCount >= 2) simState.nextGate = 2;
                            }
                        }
                    } else {
                        if (simState.gate2State === 0) {
                            const canFill = running && currentStepIsLoading;
                            if (canFill) simState.levelKg = Math.min(batchCapG1, simState.levelKg + fillKgPerTick);
                            if (simState.levelKg >= batchCapG1 && running && !downstreamBlockedRibbon)
                                simState.gate2State = 1;
                        } else {
                            simState.levelKg = Math.max(0, simState.levelKg - drainKgPerTick);
                            if (simState.levelKg <= 0) {
                                simState.levelKg = 0;
                                simState.gate2State = 0;
                                simState.nextGate = 1;
                            }
                        }
                    }
                    simState.rampValue = (simState.levelKg / batchCapG1) * 100;
                } else {
                    // ── legacy %-based mode (no batch capacity configured) ──
                    const fillPerTick_g1  = 100 / Math.max(1, cycleToTicks(loadingDurationS_g1, speedup));
                    const dischargeS_g1   = parseFloat(simCfg.sim_discharge_duration_s ?? 0);
                    const drainPerTick_g1 = dischargeS_g1 > 0
                        ? 100 / Math.max(1, cycleToTicks(dischargeS_g1, speedup))
                        : fillPerTick_g1 * 5;
                    if (simState.nextGate === 1) {
                        if (simState.gateState === 0) {
                            const canFill = running && currentStepIsLoading;
                            simState.rampValue = Math.min(100, simState.rampValue + (canFill ? fillPerTick_g1 : 0));
                            if (simState.rampValue >= 100 && running && !downstreamBlockedRibbon)
                                simState.gateState = 1;
                        } else {
                            simState.rampValue = Math.max(0, simState.rampValue - drainPerTick_g1);
                            if (simState.rampValue <= 0) {
                                simState.gateState = 0;
                                if (gateCount >= 2) simState.nextGate = 2;
                            }
                        }
                    } else {
                        if (simState.gate2State === 0) {
                            const canFill = running && currentStepIsLoading;
                            simState.rampValue = Math.min(100, simState.rampValue + (canFill ? fillPerTick_g1 : 0));
                            if (simState.rampValue >= 100 && running && !downstreamBlockedRibbon)
                                simState.gate2State = 1;
                        } else {
                            simState.rampValue = Math.max(0, simState.rampValue - drainPerTick_g1);
                            if (simState.rampValue <= 0) {
                                simState.gate2State = 0;
                                simState.nextGate = 1;
                            }
                        }
                    }
                }
                lv.gate_1 = simState.gateState;
                if (_dbg) console.log(`[RIBBON] id=${nodeId} level=${simState.rampValue.toFixed(1)}% ${batchCapG1 > 0 ? `(${(simState.levelKg??0).toFixed(0)}/${batchCapG1}kg)` : ''} gate1=${simState.gateState} gate2=${simState.gate2State??0} nextGate=${simState.nextGate} blocked=${downstreamBlockedRibbon} running=${running} loading=${currentStepIsLoading} step=${simState.recipeStep??0}`);
                break;
            }

            case 'gate_2': {
                // gate_2 state is managed by the gate_1 case above; just output it.
                const gateCount = parseInt(config.gate_count ?? 0);
                lv.gate_2 = gateCount >= 2 ? (simState.gate2State ?? 0) : 0;
                break;
            }

            case 'outlet_state': {
                // Cooler blender outlet gate.
                // Level fills ONLY when upstream ribbon_blender gate that connects to THIS cooler
                // is open (product flowing in) — determined by sourceHandle in the pre-pass.
                //
                // Two discharge modes:
                //   - Timer-based (sim_hold_time_s > 0): After the upstream gate closes the cooler
                //     holds the batch for sim_hold_time_s seconds (processing/cooling), then opens
                //     its gate to discharge. State machine: idle → filling → holding → discharging → idle.
                //   - Legacy level-based (sim_hold_time_s absent): gate opens when level reaches 100%.
                if (simState.outletState === undefined) simState.outletState = 0;
                if (!simState.frozenSignals.has('outlet_state')) {
                    const holdTimeS = parseFloat(simCfg.sim_hold_time_s ?? 0);
                    const downstreamBlockedCooler = simCfg.sim_block_gate_if_downstream_at_capacity
                        && simState.downstreamBlocked;

                    const defaults_out = ELEMENT_SIM_DEFAULTS[elementType] || ELEMENT_SIM_DEFAULTS._default;
                    const cycleOnS_out = parseFloat(simCfg.sim_cycle_on ?? defaults_out.sim_cycle_on);
                    const fillPerTick_default = 100 / Math.max(1, cycleToTicks(cycleOnS_out, speedup));
                    const receivingProduct = simState.upstreamGateOpen ?? running;
                    const fillPerTick_out = receivingProduct
                        ? (simState.upstreamFillRate ?? fillPerTick_default)
                        : fillPerTick_default;
                    const dischargeS_out = parseFloat(simCfg.sim_discharge_duration_s ?? 0);

                    if (holdTimeS > 0) {
                        // ── Timer-based state machine (kg-tracked) ──────────────────────────
                        // coolerPhase: 'idle' | 'filling' | 'holding' | 'discharging'
                        // levelKg is the single source of truth; rampValue is derived.
                        if (simState.coolerPhase === undefined) simState.coolerPhase = 'idle';
                        if (simState.coolerHoldTimerTicks === undefined) simState.coolerHoldTimerTicks = 0;
                        const prevGateOpen = simState.prevUpstreamGateOpen ?? false;
                        const curGateOpen  = !!simState.upstreamGateOpen;

                        // Batch capacity in kg (from upstream mixer config)
                        const batchCap = simState.coolerBatchCapKg || 600;
                        // Init levelKg from rampValue if resuming mid-session
                        if (simState.levelKg == null) {
                            simState.levelKg = simState.rampValue * batchCap / 100;
                        }

                        // Fill rate in kg/tick: match upstream mixer's discharge speed
                        const upDisS = simState.upstreamDischargeS || 0;
                        const fillTicks = upDisS > 0
                            ? Math.max(1, cycleToTicks(upDisS, speedup))
                            : Math.max(1, cycleToTicks(cycleOnS_out, speedup));
                        const fillKgPerTick = batchCap / fillTicks;

                        const _prevPhase = simState.coolerPhase;
                        switch (simState.coolerPhase) {
                            case 'idle':
                                if (curGateOpen) {
                                    simState.coolerPhase = 'filling';
                                    simState.levelKg = Math.min(batchCap, simState.levelKg + fillKgPerTick);
                                }
                                break;

                            case 'filling':
                                if (curGateOpen) {
                                    simState.levelKg = Math.min(batchCap, simState.levelKg + fillKgPerTick);
                                }
                                // Detect upstream gate falling edge → batch fully received
                                if (!curGateOpen && prevGateOpen) {
                                    // Snap to full batch: the mixer dumps all its content
                                    simState.levelKg = batchCap;
                                    simState.coolerHoldTimerTicks = holdTimeS; // simulated seconds remaining
                                    simState.coolerPhase = 'holding';
                                }
                                break;

                            case 'holding':
                                simState.coolerHoldTimerTicks -= TICK_DURATION_S * speedup;
                                if (simState.coolerHoldTimerTicks <= 0 && !downstreamBlockedCooler) {
                                    simState.outletState = 1;
                                    simState.coolerPhase = 'discharging';
                                }
                                break;

                            case 'discharging': {
                                // Drain rate in kg/tick
                                const drainTicks = dischargeS_out > 0
                                    ? Math.max(1, cycleToTicks(dischargeS_out, speedup))
                                    : Math.max(1, fillTicks / 5);
                                const drainKgPerTick = batchCap / drainTicks;
                                const prevKg = simState.levelKg;
                                simState.levelKg = Math.max(0, simState.levelKg - drainKgPerTick);
                                const actualDrainKg = prevKg - simState.levelKg;
                                // Accumulate kg drained this tick for computeMaterialFlows
                                if (actualDrainKg > 0) {
                                    simState.coolerDrainedKgThisTick =
                                        (simState.coolerDrainedKgThisTick ?? 0) + actualDrainKg;
                                }
                                if (simState.levelKg <= 0) {
                                    simState.levelKg = 0;
                                    simState.outletState = 0;
                                    simState.coolerPhase = 'idle';
                                }
                                break;
                            }
                        }
                        // rampValue is derived from levelKg (visual indicator)
                        simState.rampValue = batchCap > 0 ? (simState.levelKg / batchCap) * 100 : 0;
                        simState.prevUpstreamGateOpen = curGateOpen;
                        if (_dbg || _prevPhase !== simState.coolerPhase) {
                            console.log(`[COOLER] id=${nodeId} ${_prevPhase}→${simState.coolerPhase} levelKg=${simState.levelKg.toFixed(1)}/${batchCap}kg (${simState.rampValue.toFixed(1)}%) gate=${curGateOpen?'OPEN':'closed'} holdSecsLeft=${(simState.coolerHoldTimerTicks??0).toFixed(0)}s drainedKg=${(simState.coolerDrainedKgThisTick??0).toFixed(1)}`);
                        }

                    } else {
                        // ── Legacy level-based behavior ─────────────────────────────────────
                        const fillRate  = receivingProduct ? fillPerTick_out : 0;
                        const drainPerTick_out = dischargeS_out > 0
                            ? 100 / Math.max(1, cycleToTicks(dischargeS_out, speedup))
                            : fillPerTick_out * 5;
                        const drainRate = simState.outletState === 1 ? drainPerTick_out : 0;
                        simState.rampValue = Math.min(100, Math.max(0, simState.rampValue + fillRate - drainRate));
                        if (simState.outletState === 0 && simState.rampValue >= 100 && running && !downstreamBlockedCooler)
                            simState.outletState = 1;
                        else if (simState.outletState === 1 && simState.rampValue <= 0) simState.outletState = 0;
                    }
                }
                lv.outlet_state = simState.outletState;
                // is_holding=1 while the cooler is actively processing (holding timer or discharging).
                // Fires one tick AFTER the upstream gate closes, avoiding the one-tick overlap where
                // the cyclone would otherwise start while the cooler is still in the FILLING phase.
                if (parseFloat(simCfg.sim_hold_time_s ?? 0) > 0) {
                    lv.is_holding = (simState.coolerPhase === 'holding' || simState.coolerPhase === 'discharging') ? 1 : 0;
                }
                // Expose internal level so trigger system can use level_above_20 / level_below_20
                if (signalKeys.includes('level')) lv.level = simState.rampValue;
                break;
            }

            default:
                lv[key] = 0;
        }
    }

    // ── I2: Safety interlocks (post-signal) ────────────────────────────────
    // These run AFTER all signals are computed so they can read final values.
    const interlockCfg = simCfg.sim_interlocks !== false; // enabled by default
    if (interlockCfg) {
        // --- High-high level (98%): emit alarm, signal upstream to stop ---
        if (lv.level != null) {
            const hhThreshold = parseFloat(simCfg.sim_hh_level ?? 98);
            const llThreshold = parseFloat(simCfg.sim_ll_level ?? 2);
            lv.alarm_hh = lv.level >= hhThreshold ? 1 : 0;
            // Low-low level: dry-run protection for pumps/screws
            lv.alarm_ll = (lv.level <= llThreshold && lv.level > 0) ? 1 : 0;
        }

        // --- Motor overload (>115% rated for sustained period) ---
        if (lv.current != null && running) {
            const ratedCurrent = bases.current || 10;
            const overloadPct = parseFloat(simCfg.sim_overload_pct ?? 115);
            const overloadThreshold = ratedCurrent * (overloadPct / 100);
            const tripTicks = parseInt(simCfg.sim_overload_trip_ticks ?? 5);
            if (lv.current > overloadThreshold) {
                simState._overloadTicks = (simState._overloadTicks ?? 0) + 1;
            } else {
                simState._overloadTicks = 0;
            }
            lv.alarm_overload = simState._overloadTicks >= tripTicks ? 1 : 0;
            // Trip: force equipment OFF after sustained overload
            if (lv.alarm_overload && !simState.frozenSignals.has('state')) {
                lv.state = 0;
                simState.tripped = true;
            }
        } else if (!running) {
            simState._overloadTicks = 0;
        }

        // --- High temperature alarm ---
        if (lv.temp != null) {
            const tempHighThreshold = parseFloat(simCfg.sim_temp_high ?? 80);
            lv.alarm_temp_high = lv.temp >= tempHighThreshold ? 1 : 0;
        }
    }

    return lv;
}

// ── Helper: search downstream nodes for an active signal ──────────────────
// Accepts pre-built maps (outEdgeMap, nodeMap) to avoid O(N) rebuilds per recursive call.
function hasDownstreamSignal(nodeId, signal, liveMap, outEdgeMap, nodeMap, depth = 5) {
    if (depth <= 0) return false;
    const outEdges = outEdgeMap[nodeId] || [];
    for (const edge of outEdges) {
        const lv = liveMap[edge.target] || nodeMap[edge.target]?.data?.liveValues || {};
        if (parseFloat(lv[signal] ?? 0) >= 0.5) return true;
        if (hasDownstreamSignal(edge.target, signal, liveMap, outEdgeMap, nodeMap, depth - 1)) return true;
    }
    return false;
}

/**
 * Like hasDownstreamSignal but BLOCKS traversal through any node whose ID is in blockIds.
 * Used for exclusive valve groups so each valve only sees its own circuit:
 *   - valve_1 is blocked at valve_2 → can only find signals behind valve_1
 *   - valve_2 is blocked at valve_1 → can only find signals behind valve_2
 */
function hasExclusiveDownstreamSignal(nodeId, signal, liveMap, outEdgeMap, nodeMap, blockIds, depth = 5) {
    if (depth <= 0) return false;
    const outEdges = outEdgeMap[nodeId] || [];
    for (const edge of outEdges) {
        if (blockIds.has(edge.target)) continue; // don't cross sibling valves
        const lv = liveMap[edge.target] || nodeMap[edge.target]?.data?.liveValues || {};
        if (parseFloat(lv[signal] ?? 0) >= 0.5) return true;
        if (hasExclusiveDownstreamSignal(edge.target, signal, liveMap, outEdgeMap, nodeMap, blockIds, depth - 1)) return true;
    }
    return false;
}

function hasUpstreamSignal(nodeId, signal, liveMap, inEdgeMap, nodeMap, depth = 5) {
    if (depth <= 0) return false;
    const inEdges = inEdgeMap[nodeId] || [];
    for (const edge of inEdges) {
        const lv = liveMap[edge.source] || nodeMap[edge.source]?.data?.liveValues || {};
        if (parseFloat(lv[signal] ?? 0) >= 0.5) return true;
        if (hasUpstreamSignal(edge.source, signal, liveMap, inEdgeMap, nodeMap, depth - 1)) return true;
    }
    return false;
}

// ── Feature 3: Process Recipe Steps ───────────────────────────────────────
function tickRecipeSteps(node, simState, speedup, running, lv) {
    const simCfg = node.data?.config?.sim || {};
    const steps = simCfg.sim_recipe_steps;
    if (!steps || !Array.isArray(steps) || steps.length === 0) return;

    if (!running) {
        // Grace period: only reset the recipe after the machine has been OFF for more than
        // 2 ticks. This prevents the 1-tick gap produced by sim_cycle_off:0 from wiping
        // the current recipe position mid-batch.
        if (simState.recipeStoppedTicks == null) simState.recipeStoppedTicks = 0;
        simState.recipeStoppedTicks += TICK_DURATION_S * speedup;
        if (simState.recipeStoppedTicks > 5) { // 5 simulated seconds grace period
            simState.recipeStep = 0;
            simState.stepTicksRemaining = 0;
            simState.recipeStoppedTicks = 0;
        }
        steps.forEach(s => { if (s.signal) lv[s.signal] = 0; });
        return;
    }
    simState.recipeStoppedTicks = 0; // clear counter on resumption

    // Init step timer on first run or after reset
    if (!simState.stepTicksRemaining || simState.stepTicksRemaining <= 0) {
        const step = steps[simState.recipeStep];
        if (!step) { simState.recipeStep = 0; }
        const durationS = parseFloat(steps[simState.recipeStep]?.duration_s ?? 60);
        simState.stepTicksRemaining = durationS; // stored as simulated seconds remaining
    }

    // Emit current step's signal, clear others
    steps.forEach((s, i) => {
        if (s.signal) lv[s.signal] = (i === simState.recipeStep) ? 1 : 0;
    });

    simState.stepTicksRemaining -= TICK_DURATION_S * speedup;
    if (simState.stepTicksRemaining <= 0) {
        simState.recipeStep = (simState.recipeStep + 1) % steps.length;
        const nextDurationS = parseFloat(steps[simState.recipeStep]?.duration_s ?? 60);
        simState.stepTicksRemaining = nextDurationS; // stored as simulated seconds remaining
    }
}

/**
 * Main export: compute new liveValues for ALL nodes in one tick.
 *
 * @param {Array}  allNodes      - ReactFlow nodes array (current state)
 * @param {Array}  edges         - ReactFlow edges array
 * @param {Object} simStates     - { [nodeId]: simState } — mutated in place
 * @param {Object} SIGNAL_CONFIG - imported from port_config.js
 * @param {number} speedup       - time acceleration factor (default 60)
 * @returns {Object}             - { [nodeId]: liveValues }
 */
// Global tick counter for debug logging (logs every N ticks)
let _debugTickCounter = 0;
const _DEBUG_INTERVAL = 5; // log every 5 ticks
let _dbg = false; // module-level so tickNode/computeMaterialFlows can read it

export function tickSimulationAll(allNodes, edges, simStates, SIGNAL_CONFIG, speedup = 60) {
    _debugTickCounter++;
    _dbg = (_debugTickCounter % _DEBUG_INTERVAL === 0);

    // Pre-build graph lookup maps once — used by hasDownstreamSignal/hasUpstreamSignal
    // to avoid O(N) rebuilds per recursive call (BUG-3 fix).
    const nodeMap = {};
    const outEdgeMap = {};
    const inEdgeMap = {};
    allNodes.forEach(n => { nodeMap[n.id] = n; outEdgeMap[n.id] = []; inEdgeMap[n.id] = []; });
    edges.forEach(e => {
        if (outEdgeMap[e.source]) outEdgeMap[e.source].push(e);
        if (inEdgeMap[e.target]) inEdgeMap[e.target].push(e);
    });

    // Tier 1: graph topology
    const flowFractions = computeFlowFractions(allNodes, edges);

    // Pre-pass: Feature 7 — set downstreamBlocked flag from PREVIOUS tick's liveValues
    // so gate logic inside tickNode can read it without needing edges/allNodes.
    allNodes.forEach(node => {
        const simCfg = node.data?.config?.sim || {};
        const st = simStates[node.id];
        if (!st) return;
        if (simCfg.sim_check_downstream_capacity_before_gate
                || simCfg.sim_block_gate_if_downstream_at_capacity) {
            const downEdges = edges.filter(e => e.source === node.id);
            st.downstreamBlocked = downEdges.some(edge => {
                const downNode = allNodes.find(n => n.id === edge.target);
                const lv = downNode?.data?.liveValues || {};
                return parseFloat(lv.at_capacity ?? 0) >= 0.5
                    || parseFloat(lv.alarm_hh ?? 0) >= 0.5;
            });
        }

        // Pre-pass: temperature interlock — ribbon_blender waits for downstream cooler to reach setpoint.
        // Reads cooler's temp_ready from the PREVIOUS tick's liveValues (one-tick lag is acceptable).
        if (node.type === 'ribbon_blender' && (node.data?.config?.sim?.sim_require_downstream_temp_ready)) {
            const downCoolers = edges
                .filter(e => e.source === node.id)
                .map(e => allNodes.find(n => n.id === e.target))
                .filter(n => n?.type === 'cooler_blender');
            st.tempInterlockBlocked = downCoolers.length > 0 && downCoolers.some(
                c => parseFloat(c.data?.liveValues?.temp_ready ?? 0) < 0.5
            );
        } else {
            st.tempInterlockBlocked = false;
        }

        // Pre-pass: cooler_blender upstream gate check.
        // Cooler level fills ONLY when the upstream ribbon_blender gate that connects to THIS cooler
        // is open (product flowing in). Uses the edge's sourceHandle to determine which gate is relevant:
        //   sourceHandle='out1' or 'out' → gate_1, sourceHandle='out2' → gate_2
        // This prevents a cooler connected to gate_2 from filling when gate_1 is open (and vice-versa).
        if (node.type === 'cooler_blender') {
            const inEdges = edges.filter(e => e.target === node.id);
            st.upstreamGateOpen = false;
            st.chillerConnected = false;
            st.chillerSupplyTemp = null;
            for (const edge of inEdges) {
                const upNode = allNodes.find(n => n.id === edge.source);
                if (!upNode) continue;

                // Chilled water connection: chiller's chw_out → cooler's chw_in
                if (edge.targetHandle === 'chw_in' && upNode.type === 'chiller') {
                    const chillerLv = upNode.data?.liveValues || {};
                    const chillerOn = parseFloat(chillerLv.state ?? 0) >= 0.5;
                    if (chillerOn) {
                        st.chillerConnected = true;
                        st.chillerSupplyTemp = parseFloat(chillerLv.temp_supply ?? 7);
                    }
                    continue;
                }

                // Product connection: upstream ribbon_blender gate check
                const lv = upNode?.data?.liveValues || {};
                // Determine which gate on the upstream mixer feeds THIS cooler
                const handle = edge.sourceHandle || 'out1';
                const isGate1 = (handle === 'out1' || handle === 'out');
                const gateSignal = isGate1 ? 'gate_1' : 'gate_2';
                if (parseFloat(lv[gateSignal] ?? 0) >= 0.5) {
                    st.upstreamGateOpen = true;
                    const upSimCfg = upNode.data?.config?.sim || {};
                    const upDischargeS = parseFloat(upSimCfg.sim_discharge_duration_s ?? 0);
                    // Store upstream discharge duration so the cooler state machine
                    // can compute fill rate in kg/tick (matched to mixer drain rate).
                    st.upstreamDischargeS = upDischargeS;
                    // Keep legacy %/tick fill rate for non-kg mode
                    if (upDischargeS > 0) {
                        st.upstreamFillRate = 100 / Math.max(1, cycleToTicks(upDischargeS, speedup));
                    }
                    // Store mixer batch capacity (kg) — source of truth for cooler's levelKg.
                    st.coolerBatchCapKg = parseFloat(upSimCfg.sim_batch_capacity_kg ?? 0);
                    // P2: hot batch temperature from mixer config (for cooler thermal model)
                    st.hotBatchTemp = parseFloat(upSimCfg.sim_outlet_temp ?? 55);
                }
            }
        }
        // 5.2: Chiller thermal load — detect if any connected cooler_blender is actively cooling.
        // The chiller works harder (higher return temp, higher pressures) when absorbing heat.
        if (node.type === 'chiller') {
            let thermalLoad = 0; // 0 = idle, 0–1 = proportional to cooling demand
            const downEdges = outEdgeMap[node.id] || [];
            for (const edge of downEdges) {
                const downNode = nodeMap[edge.target];
                if (!downNode || downNode.type !== 'cooler_blender') continue;
                const dSt = simStates[downNode.id];
                if (!dSt) continue;
                // Holding = actively absorbing heat; filling = partial load
                if (dSt.coolerPhase === 'holding') {
                    // Load proportional to deltaT between product and chiller water
                    const coolerTarget = parseFloat(downNode.data?.config?.sim?.sim_cooler_target ?? 8);
                    const productTemp = dSt.coolerTemp ?? 30;
                    const deltaT = Math.max(0, productTemp - coolerTarget);
                    const nominalDelta = 50; // ~55°C product - ~6°C water
                    thermalLoad = Math.max(thermalLoad, Math.min(1, deltaT / nominalDelta));
                } else if (dSt.coolerPhase === 'filling') {
                    thermalLoad = Math.max(thermalLoad, 0.3); // partial load during filling
                }
            }
            st.chillerThermalLoad = thermalLoad;
        }
    });

    // Tier 2: per-node value generation
    const newLiveValuesMap = {};
    allNodes.forEach(node => {
        const signals = SIGNAL_CONFIG[node.type];
        if (!signals || signals.length === 0) return;
        const simState = simStates[node.id];
        if (!simState) return;
        const signalKeys = signals.map(s => s.key);
        const flowFraction = flowFractions[node.id] ?? 1;
        newLiveValuesMap[node.id] = tickNode({
            node,
            nodeId: node.id,
            elementType: node.type,
            config: node.data?.config || {},
            simState,
            signalKeys,
            flowFraction,
            speedup,
            outEdgeMap,
            newLiveValuesMap,
            nodeMap,
            allNodes,
            allEdges: edges,
        });
    });

    // Feature 3: Process recipe steps (runs after tickNode so state is resolved)
    allNodes.forEach(node => {
        const simState = simStates[node.id];
        const lv = newLiveValuesMap[node.id];
        if (!simState || !lv) return;
        const running = parseFloat(lv.state ?? 0) >= 0.5;
        tickRecipeSteps(node, simState, speedup, running, lv);
    });

    // Feature 3b: Recipe step resource blocking (block_if_upstream_signal)
    // If the active recipe step declares "block_if_upstream_signal", check whether any upstream
    // node is emitting that signal (e.g., caramel tank at_empty). If so, freeze the step timer
    // and set the machine to state=0 (paused) until the resource is available again.
    allNodes.forEach(node => {
        const simState = simStates[node.id];
        const lv = newLiveValuesMap[node.id];
        if (!simState || !lv) return;
        if (parseFloat(lv.state ?? 0) < 0.5) return; // already stopped
        const steps = node.data?.config?.sim?.sim_recipe_steps;
        if (!steps || !Array.isArray(steps)) return;
        const currentStep = steps[simState.recipeStep];
        const blockSignal = currentStep?.block_if_upstream_signal;
        if (!blockSignal) return;
        const blocked = hasUpstreamSignal(node.id, blockSignal, newLiveValuesMap, inEdgeMap, nodeMap, 6);
        if (blocked) {
            lv.state = 0;
            simState.currentBoolValues.state = 0;
            // Undo the timer decrement done in tickRecipeSteps so the step doesn't advance
            if (simState.stepTicksRemaining != null) simState.stepTicksRemaining++;
        }
    });

    // Trigger system: apply after all values computed this tick
    allNodes.forEach(node => {
        const simCfg = node.data?.config?.sim || {};
        const triggers = simCfg.sim_triggers || [];
        if (!triggers.length) return;

        // Per-node storage for edge-detection trigger conditions (persists across ticks)
        const simState = simStates[node.id];
        if (!simState) return;
        if (!simState._triggerPrev) simState._triggerPrev = {};

        triggers.forEach((trigger, triggerIdx) => {
            const watchedNode = allNodes.find(n => n.id === trigger.watch_node_id);
            if (!watchedNode) return;
            const watchedLV = newLiveValuesMap[trigger.watch_node_id] || watchedNode.data?.liveValues || {};
            const watchedThreshold = parseFloat(watchedNode.data?.config?.threshold ?? 0.5);

            // Per-trigger slot for previous values (indexed so two triggers on the same node don't collide)
            const prevKey = `${triggerIdx}_${trigger.watch_node_id}`;
            const prevLV  = simState._triggerPrev[prevKey] || {};

            // Helper shorthands
            const cur  = (sig) => parseFloat(watchedLV[sig] ?? 0);
            const prev = (sig) => parseFloat(prevLV[sig]    ?? 0);
            const thr  = watchedThreshold;

            let conditionMet = false;
            switch (trigger.condition) {
                // ── Level / state — steady conditions ──────────────────────
                case 'state_on':       conditionMet = cur('state') >= thr; break;
                case 'state_off':      conditionMet = cur('state') <  thr; break;
                case 'level_above_80': conditionMet = cur('level') >= 80;  break;
                case 'level_below_20': conditionMet = cur('level') <= 20;  break;
                // ── Gate steady conditions ──────────────────────────────────
                case 'gate_1_open':    conditionMet = cur('gate_1')       >= thr; break;
                case 'gate_1_closed':  conditionMet = cur('gate_1')       <  thr; break;
                case 'gate_2_open':    conditionMet = cur('gate_2')       >= thr; break;
                case 'gate_2_closed':  conditionMet = cur('gate_2')       <  thr; break;
                case 'gate_open':      conditionMet = cur('outlet_state') >= thr; break;
                case 'gate_closed':    conditionMet = cur('outlet_state') <  thr; break;
                // ── Edge conditions (fire once on transition) ───────────────
                // gate_1: rising edge = just opened (0→1), falling edge = just closed (1→0)
                case 'gate_1_rising':  conditionMet = prev('gate_1') < thr && cur('gate_1') >= thr; break;
                case 'gate_1_falling': conditionMet = prev('gate_1') >= thr && cur('gate_1') < thr; break;
                // gate_2
                case 'gate_2_rising':  conditionMet = prev('gate_2') < thr && cur('gate_2') >= thr; break;
                case 'gate_2_falling': conditionMet = prev('gate_2') >= thr && cur('gate_2') < thr; break;
                // outlet_state (cooler gate)
                case 'gate_rising':    conditionMet = prev('outlet_state') < thr && cur('outlet_state') >= thr; break;
                case 'gate_falling':   conditionMet = prev('outlet_state') >= thr && cur('outlet_state') < thr; break;
                // is_holding (cooler processing phase: holding+discharging=1, idle+filling=0)
                case 'holding_rising':  conditionMet = prev('is_holding') < thr && cur('is_holding') >= thr; break;
                case 'holding_falling': conditionMet = prev('is_holding') >= thr && cur('is_holding') < thr; break;
                // state
                case 'state_rising':   conditionMet = prev('state') < thr && cur('state') >= thr; break;
                case 'state_falling':  conditionMet = prev('state') >= thr && cur('state') < thr; break;
                default: break;
            }

            // Save current watched values for next tick's edge detection
            simState._triggerPrev[prevKey] = { ...watchedLV };

            if (conditionMet) {
                const targetState = trigger.action === 'start' ? 1 : 0;
                if (newLiveValuesMap[node.id]) newLiveValuesMap[node.id].state = targetState;
                simState.currentBoolValues.state = targetState;
                const defaults = ELEMENT_SIM_DEFAULTS[node.type] || ELEMENT_SIM_DEFAULTS._default;
                const nextSeconds = targetState === 1
                    ? (simCfg.sim_cycle_on  ?? defaults.sim_cycle_on)
                    : (simCfg.sim_cycle_off ?? defaults.sim_cycle_off);
                simState.boolTimers.state = nextSeconds; // simulated seconds remaining
            }
        });
    });

    // Feature 4: Demand-driven pumps/valves (sim_run_on_downstream_signal / sim_open_on_downstream_signal)
    // For valves in an exclusive group: use circuit-isolated traversal so each valve only sees
    // its own downstream path (blocked at sibling valves). This prevents both valves activating
    // when their circuits share any downstream node (e.g. a common hopper).
    const EXCLUSIVE_VALVE_TYPES = new Set(['valve_gate', 'valve_ball', 'valve_butterfly', 'valve_3way']);
    allNodes.forEach(node => {
        const simCfg = node.data?.config?.sim || {};
        const watchSignal = simCfg.sim_run_on_downstream_signal || simCfg.sim_open_on_downstream_signal;
        if (!watchSignal) return;
        const simState = simStates[node.id];
        if (!simState || simState.frozenSignals.has('state')) return;

        let active;
        const exclusiveGroup = simCfg.sim_exclusive_valve_group;
        if (exclusiveGroup && EXCLUSIVE_VALVE_TYPES.has(node.type)) {
            // Build set of sibling valve IDs (other valves in the same exclusive group).
            // Traversal is blocked at those IDs so this valve only sees its own circuit.
            const siblingIds = new Set(
                allNodes
                    .filter(n => n.id !== node.id
                        && EXCLUSIVE_VALVE_TYPES.has(n.type)
                        && n.data?.config?.sim?.sim_exclusive_valve_group === exclusiveGroup)
                    .map(n => n.id)
            );
            active = hasExclusiveDownstreamSignal(node.id, watchSignal, newLiveValuesMap, outEdgeMap, nodeMap, siblingIds);
        } else {
            active = hasDownstreamSignal(node.id, watchSignal, newLiveValuesMap, outEdgeMap, nodeMap);
        }

        const targetState = active ? 1 : 0;
        if (newLiveValuesMap[node.id]) newLiveValuesMap[node.id].state = targetState;
        simState.currentBoolValues.state = targetState;
    });

    // Feature 5: Exclusive valve group — safety pass.
    // Feature 4 with circuit-isolated traversal should already ensure at most one valve per group
    // is active. This pass is a fallback: if somehow multiple are open, keep the one that has
    // the signal in its exclusive circuit; FIFO otherwise.
    const exclusiveGroups = {};
    allNodes.forEach(node => {
        if (!EXCLUSIVE_VALVE_TYPES.has(node.type)) return;
        const group = node.data?.config?.sim?.sim_exclusive_valve_group;
        if (!group) return;
        if (!exclusiveGroups[group]) exclusiveGroups[group] = [];
        exclusiveGroups[group].push(node);
    });
    Object.values(exclusiveGroups).forEach(members => {
        const openOnes = members.filter(n => parseFloat(newLiveValuesMap[n.id]?.state ?? 0) >= 0.5);
        if (openOnes.length <= 1) return;
        // Pick the valve whose exclusive circuit has the active signal.
        const watchSignal = members.find(n => n.data?.config?.sim?.sim_open_on_downstream_signal)
            ?.data?.config?.sim?.sim_open_on_downstream_signal;
        let winner = null;
        if (watchSignal) {
            const memberIds = new Set(members.map(n => n.id));
            winner = openOnes.find(n => {
                const siblings = new Set([...memberIds].filter(id => id !== n.id));
                return hasExclusiveDownstreamSignal(n.id, watchSignal, newLiveValuesMap, outEdgeMap, nodeMap, siblings);
            });
        }
        if (!winner) winner = openOnes[0]; // FIFO fallback
        openOnes.filter(n => n.id !== winner.id).forEach(n => {
            if (newLiveValuesMap[n.id]) newLiveValuesMap[n.id].state = 0;
            const st = simStates[n.id];
            if (st) st.currentBoolValues.state = 0;
        });
    });

    // Feature 6: Estimated tank consumption — decrement level on falling edge of downstream signal
    allNodes.forEach(node => {
        const simCfg = node.data?.config?.sim || {};
        if (simCfg.sim_consumption_mode !== 'estimated') return;
        const triggerSignal = simCfg.sim_consumption_trigger_signal;
        const kgPerBatch = parseFloat(simCfg.sim_consumption_per_batch_kg ?? 0);
        if (!triggerSignal || kgPerBatch <= 0) return;
        const simState = simStates[node.id];
        if (!simState) return;
        const wasFiring = simState.consumptionSignalWasActive ?? false;
        const isFiring  = hasDownstreamSignal(node.id, triggerSignal, newLiveValuesMap, outEdgeMap, nodeMap);
        if (wasFiring && !isFiring) {
            // Falling edge → consume one batch
            const cap = parseFloat(simCfg.sim_capacity_kg ?? 1000);
            if (simState.levelKg == null)
                simState.levelKg = parseFloat(simCfg.sim_initial_level_kg ?? cap);
            simState.levelKg = Math.max(0, simState.levelKg - kgPerBatch);
            const pct = (simState.levelKg / cap) * 100;
            simState.rampValue = pct;
            if (newLiveValuesMap[node.id]) newLiveValuesMap[node.id].level = pct;
        }
        simState.consumptionSignalWasActive = isFiring;
    });

    // Tier 2b: material flow — elevator transport, container levels, packager buffers, cap sensors
    computeMaterialFlows(allNodes, edges, simStates, newLiveValuesMap, speedup);

    // Elevator → packager/container demand link:
    // If the elevator is sim_always_on, skip this override (tickNode already set state=1 always).
    // Otherwise, auto-start the elevator when any downstream packager is requesting product.
    allNodes.filter(n => n.type === 'bucket_elevator').forEach(elevator => {
        const simState = simStates[elevator.id];
        if (!simState || simState.frozenSignals.has('state')) return;

        // sim_always_on elevators never need demand-driven override
        const elevSimCfg = elevator.data?.config?.sim || {};
        if (elevSimCfg.sim_always_on) return;

        const downstreamNodes = edges
            .filter(e => e.source === elevator.id)
            .map(e => allNodes.find(n => n.id === e.target))
            .filter(Boolean);

        if (downstreamNodes.length === 0) return;

        // Check requesting_product from direct downstream OR through intermediate containers (e.g. hopper → packager)
        const anyRequesting = hasDownstreamSignal(elevator.id, 'requesting_product', newLiveValuesMap, outEdgeMap, nodeMap, 3);

        const targetState = anyRequesting ? 1 : 0;
        if (newLiveValuesMap[elevator.id]) newLiveValuesMap[elevator.id].state = targetState;
        simState.currentBoolValues.state = targetState;
    });

    // Feature 7 post-pass: propagate at_capacity / at_empty upward via newLiveValuesMap so that
    // next tick's pre-pass (downstreamBlocked) reads the freshest values.
    // Also emit at_capacity for containers whose level is now >= 99% after material flows,
    // and at_empty for containers whose level is now <= 0% (sim_emit_at_empty: true).
    allNodes.forEach(node => {
        const simCfg = node.data?.config?.sim || {};
        const lv = newLiveValuesMap[node.id];
        const simState = simStates[node.id];
        if (!lv || !simState) return;
        if (simCfg.sim_emit_at_capacity) lv.at_capacity = simState.rampValue >= 99 ? 1 : 0;
        if (simCfg.sim_emit_at_empty)    lv.at_empty    = simState.rampValue <= 0  ? 1 : 0;
    });

    // DEBUG: summary of all containers and key nodes
    if (_dbg) {
        const summary = [];
        allNodes.forEach(n => {
            const st = simStates[n.id];
            const lv = newLiveValuesMap[n.id];
            if (!st || !lv) return;
            if (CONTAINER_TYPES.has(n.type)) {
                const cap = parseFloat(n.data?.config?.sim?.sim_capacity_kg ?? 0);
                summary.push(`${n.id}(${n.type.substring(0,6)}) ${(st.levelKg??st.rampValue).toFixed(0)}${cap?'/'+cap+'kg':'%'} req=${lv.requesting_product??'-'}`);
            }
        });
        console.log(`[TICK ${_debugTickCounter}] spd=${speedup} containers: ${summary.join(' | ')}`);
        // DEBUG: granulator state for BFS flow investigation
        allNodes.filter(n => n.type === 'granulator').forEach(n => {
            const lv = newLiveValuesMap[n.id];
            const st = simStates[n.id];
            const cfg = n.data?.config?.sim || {};
            console.log(`[GRANULATOR] id=${n.id} state=${lv?.state} alwaysOn=${cfg.sim_always_on} stateCV=${st?.currentBoolValues?.state}`);
        });
    }

    return newLiveValuesMap;
}
